/* ==========================================================================
   FIGS global navigation — promo bar, masthead, compact sticky header,
   Women mega-menu (desktop) and full-screen nav drawer (mobile).

   Source: the Navigation demo (experiments/09-navigation), namespaced `fnav-`
   and packaged so the Homepage and PLP prototypes share one implementation.
   The promo bar's right-hand links (country selector + Find a Hub) follow the
   Homepage prototype.

     import { mountFigsNav } from '/src/figs-nav/figs-nav.js';
     mountFigsNav({ promo: el, masthead: el, footer: el, theme: 'dark' | 'light' });

   `promo` / `masthead` / `footer` are placeholder elements the page positions;
   each is replaced by the real markup (`footer` is optional — most prototypes
   carry their own). The compact bar, mega-menu and mobile drawer
   are fixed-position and appended to <body>.

   The bag (figs-cart.js) mounts with it: the three Bag icons open the cart
   drawer, and a page's add-to-bag calls addToFigsBag(), re-exported here so
   pages import the nav and nothing else. So does the account flow
   (figs-account.js): the three Account icons, and the menus' My Account
   links, open Log In / Sign Up / Forgot Password / Complete Profile, or the
   signed-in My Account drawer.
   ========================================================================== */

import { animate, stagger } from 'https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm';
import { mountFigsCart } from './figs-cart.js';
import { mountFigsAccount, openFigsAccount, isFigsSignedIn } from './figs-account.js';
import './figs-nav.css';
import { footerHTML, mountFigsFooter } from './figs-footer.js';

export {
  addToFigsBag, openFigsBag, closeFigsBag, flyToFigsBag, figsBagCount, setFigsNavBagCount,
} from './figs-cart.js';
export { openFigsAccount, closeFigsAccount, isFigsSignedIn, figsSavedSize } from './figs-account.js';

const Motion = { animate, stagger };

const asset = (name) => new URL(`./assets/${name}`, import.meta.url).href;
const ASSETS = {
  wordmark: asset('figs-wordmark.png'),
  flag: asset('icon-flag-usa.svg'),
  scrubs: [asset('scrubs-1.png'), asset('scrubs-2.png'), asset('scrubs-3.png')],
  royalBlue: [asset('royal-blue-1.png'), asset('royal-blue-2.png'), asset('royal-blue-3.png')],
};

function fragment(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content;
}

// Every FIGS logo (masthead, compact bar, mega-menu, mobile drawer) links to
// the Motion Lab index, where the template prototypes are listed — on every
// prototype, so reviewers always have one way back. Pages used to pass their
// own `homeHref`; it's still accepted but ignored, so one change here covers
// every prototype without touching their pages.
const LAB_HOME = '/';

// The lab's prototypes, by what they show. Every link in the nav whose
// content the lab has built goes there, so a reviewer moves through the lab
// the way they would the site, without going back to its index (treed,
// 2026-10-18); a link to something the lab hasn't built stays a placeholder
// (#). One table, so the next prototype is one line.
const LAB = {
  homepage: '/experiments/08-d-homepage/',
  scrubPants: '/experiments/03-plp-prototype/',         // Women's Scrub Pants (the Core pants)
  classicFit: '/experiments/11-plp-classic-fit/',       // Women's Classic Fit, by fabric: FIONx™ / FIBREx™ / FORMx™
  menCore: '/experiments/12-plp-core/',                 // Men's Core: tops, pants, outerwear …
  charcoal: '/experiments/13-plp-charcoal/',            // Women's Charcoal
  scrubSets: '/experiments/14-plp-scrub-sets/',         // Women's Scrub Sets
  topPdp: '/experiments/10-pdp-prototype/',             // Catarina One-Pocket Scrub Top™ (?color=)
  pantPdp: '/experiments/10-pdp-prototype/pants.html',  // Isabel High Waisted Wide-Leg Scrub Pants (?color=)
  mensSet: '/experiments/10-pdp-prototype/mens-set.html', // Men's Jogger Set: Leon + Tansen (?color=)
  builder: '/experiments/15-uniform-builder/',
  embroidery: '/experiments/16-embroidery/',
};
// "Build Your Uniform" (mega-menu and mobile drawer) opens the Uniform Builder prototype
const UNIFORM_BUILDER = LAB.builder;
// Headings and highlights, by gender (they swap on a gender change): New
// Arrivals and Best Sellers as the bag's empty state has them, Core the
// gender's Core PLP (the PDPs' "Shop Core Collection" is the pants PLP).
const HEADING_HREFS = {
  women: { 'New Arrivals': LAB.scrubSets, 'Best Sellers': LAB.scrubPants, Core: LAB.scrubPants },
  men: { 'New Arrivals': LAB.menCore, 'Best Sellers': LAB.menCore, Core: LAB.menCore },
};
const CORE_PLP = LAB.scrubPants; // the markup's first paint (Women's); syncLabLinks() keeps it per gender
// Sub-level links with a prototype behind them, by gender then label. Men's
// Core PLP carries every men's scrub.
const SUBNAV_HREFS = {
  women: { 'Scrub Sets': LAB.scrubSets, 'Scrub Pants': LAB.scrubPants },
  men: { 'Scrub Sets': LAB.mensSet, 'All Scrubs': LAB.menCore, 'Scrub Tops': LAB.menCore, 'Scrub Pants': LAB.menCore },
};
// The masthead's Women / Men (hover opens the menu, a click goes to the
// gender's landing) and About's one link with a prototype behind it.
const GENDER_HREFS = { women: LAB.homepage, men: LAB.menCore };
const ABOUT_HREFS = { 'Personalize Your Scrubs': LAB.embroidery };

/* Shop by Color → the page that shows the colour: Charcoal has its own PLP;
   any other colour the Catarina is made in opens the Catarina PDP in it
   (10-pdp-prototype/data.js); the rest have no page in the lab yet. */
const slugOf = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const CATARINA_COLORS = new Set(['amethyst', 'berry-spice', 'black', 'blue-horizon', 'bonsai', 'burgundy',
  'caribbean-blue', 'ceil-blue', 'celery', 'creamsicle', 'dark-harbor', 'deep-purple', 'deep-reef',
  'deep-royal-blue', 'espresso', 'f-ing-pink', 'fight-club-pink', 'fireside', 'forest-green', 'graphite',
  'harbor-grey', 'hibiscus', 'lagoon', 'martini-olive', 'mauve', 'moss', 'navy', 'off-white', 'optic-white',
  'pop-red', 'purple-haze', 'rapid-response-pink', 'royal-blue', 'sagewater', 'sea-mist', 'seaglass', 'sienna',
  'smokey-taupe']);
export function labColorHref(name) {
  const slug = slugOf(name);
  if (slug === 'charcoal') return LAB.charcoal;
  return CATARINA_COLORS.has(slug) ? `${LAB.topPdp}?color=${slug}` : null;
}
/* A product named the way the lab's PDPs are — the Catarina top, the Isabel
   pant — opens that PDP, in the colour after the name's em dash. */
export function labProductHref(name) {
  const [base, color] = String(name || '').split('—');
  const q = color && slugOf(color) ? `?color=${slugOf(color)}` : '';
  if (/catarina[^—]*scrub top/i.test(base)) return LAB.topPdp + q;
  if (/isabel[^—]*scrub pant/i.test(base)) return LAB.pantPdp + q;
  return null;
}

// The site footer (figs-footer.js) — the search results page's, and every
// prototype's: mountFigsNav() swaps any page footer for it.
const siteFooter = footerHTML;

/* TEAMS (Figma "Navigation [TEAMS Tab]" 3616:164842) — a flat, static menu
   like About: three main links, then the TEAMS access block, which follows
   the shopper's sign-in (figs-account.js). Signed out it's the logged-out
   frames (D 3542:474121 / M 3609:158560): ACCESS YOUR TEAMS SOLUTION, with
   Sign in to FIGS opening the account drawer's Log In. Signed in it's the
   Team Admin State (D 3616:164541 / M 3616:164585): YOUR TEAMS ACCESS,
   Manage Storefront, and the team's card. The Team Member State
   (3610:159017 / 3609:158774) isn't wired yet. Nothing here has a page in
   the lab yet, so every other link is a placeholder (#). Desktop and mobile
   share this markup; `.fnav-teams--desktop` / `--mobile` lay it out, and
   `.is-admin` (syncTeams, in mountFigsNav) picks the state. */
const TEAMS_OUTLINK = `<span class="fnav-teams__outlink" aria-hidden="true"><img src="${asset('icon-arrow-outlink.svg')}" alt=""></span>`;
const teamsMenuHTML = (where) => `<div class="fnav-teams fnav-teams--${where}" data-fnav-teams>
  <div class="fnav-teams__menu">
    <div class="fnav-teams__main">
      <a href="#" class="fnav-nav-drawer-heading fnav-teams__link">Explore FIGS Teams</a>
      <a href="#" class="fnav-nav-drawer-heading fnav-teams__link">Teams Help Center</a>
      <a href="#" class="fnav-nav-drawer-heading fnav-teams__link">Contact Teams</a>
    </div>
    <div class="fnav-teams__access">
      <p class="fnav-teams__eyebrow"><span class="fnav-teams__eyebrow-text" data-teams-only="guest">ACCESS YOUR TEAMS SOLUTION</span><span class="fnav-teams__eyebrow-text" data-teams-only="admin">YOUR TEAMS ACCESS</span></p>
      <div class="fnav-teams__solutions">
        <div class="fnav-teams__solution">
          <div class="fnav-teams__solution-copy">
            <p class="fnav-teams__solution-name">Storefront</p>
            <p class="fnav-teams__solution-desc">Shop your team’s selected styles.</p>
          </div>
          <a href="#" class="fnav-teams__cta" data-teams-only="guest" data-fnav-teams-signin><span class="fnav-teams__cta-label">Sign in to FIGS</span></a>
          <a href="#" class="fnav-teams__cta" data-teams-only="admin"><span class="fnav-teams__cta-label">Shop Now</span></a>
          <a href="#" class="fnav-teams__cta" data-teams-only="admin"><span class="fnav-teams__cta-label">Manage Storefront</span>${TEAMS_OUTLINK}</a>
        </div>
        <div class="fnav-teams__solution">
          <div class="fnav-teams__solution-copy">
            <p class="fnav-teams__solution-name">Teams App</p>
            <p class="fnav-teams__solution-desc">Manage your group orders.</p>
          </div>
          <a href="#" class="fnav-teams__cta"><span class="fnav-teams__cta-label">Go to TEAMS App</span>${TEAMS_OUTLINK}</a>
        </div>
      </div>
    </div>
  </div>
  <a href="#" class="fnav-teams__card" data-teams-only="admin">
    <span class="fnav-teams__card-copy">
      <span class="fnav-teams__card-eyebrow">Your Team</span>
      <span class="fnav-teams__card-name">European Wax Center</span>
    </span>
  </a>
</div>`;

export function mountFigsNav({
  promo,
  masthead,
  footer,
  theme = 'dark',
} = {}) {
  if (!masthead) throw new Error('mountFigsNav: a masthead placeholder is required');

  const PROMO = `<div class="fnav-promo-bar" id="fnav-promo-bar">
  <span class="fnav-promo-text">Free Shipping &amp; Returns on Orders $50+</span>
  <div class="fnav-promo-right">
    <div class="fnav-promo-locale fnav-js-open-country-drawer">
      <img src="${ASSETS.flag}" alt="" width="16" height="16">
      <span class="fnav-promo-link">US | English</span>
    </div>
    <a class="fnav-promo-link" href="#">Find a Hub</a>
  </div>
</div>`;

  let MASTHEAD = `<div class="fnav-masthead" id="fnav-masthead">
    <div class="fnav-masthead-left" id="fnav-masthead-left">
      <button class="fnav-mobile-hamburger fnav-mobile-hamburger--hero" id="fnav-hamburger-hero" type="button" aria-label="Open menu">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 17.4165H4V15.9165H20V17.4165ZM20 12.7505H4V11.2505H20V12.7505ZM20 8.0835H4V6.5835H20V8.0835Z" fill="currentColor"/></svg>
      </button>
      <a href="${LAB_HOME}" class="fnav-logo-lockup" aria-label="FIGS home">
        <svg class="fnav-logo-mark" width="26.3" height="40" viewBox="0 0 27 41" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.3976 0.00415039C15.4741 0.025623 17.3224 0.380811 18.9152 0.973877C23.5255 2.66954 26.4062 6.70742 26.3868 13.0452V27.1917C26.3597 33.4825 23.4174 37.4784 18.8478 39.1936C17.2433 39.8122 15.0348 40.175 12.9464 40.1223C10.891 40.1418 9.03051 39.7751 7.43762 39.1819C2.86049 37.4959 -0.023103 33.4784 0.00012207 27.1331V12.9749C0.00592853 6.67225 2.94755 2.67541 7.46497 0.983643C9.11981 0.279232 11.3208 -0.040729 13.3976 0.00415039ZM10.8292 17.6829L5.38879 17.6809C5.38879 17.6809 5.45439 22.3137 10.8038 22.425L10.8175 27.886L15.4884 27.9446L15.5148 22.4407L20.9562 22.468C20.9562 22.468 20.4813 18.0849 15.5206 17.7219L15.548 12.2268L10.8546 12.2112L10.8292 17.6829Z" fill="rgb(255,255,255)"/>
        </svg>
        <img src="${ASSETS.wordmark}" alt="FIGS" class="fnav-wordmark fnav-hero-icon">
      </a>
      <nav class="fnav-nav-links fnav-nav-links--hero" aria-label="Primary">
        <a href="#" data-fnav-drawer="women" class="is-active">Women</a>
        <a href="#" data-fnav-drawer="men">Men</a>
        <a href="#" data-fnav-drawer="teams">Teams</a>
        <a href="#" data-fnav-drawer="about">About</a>
      </nav>
    </div>
    <div class="fnav-masthead-icons">
      <svg class="fnav-icon-search" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Search"><path d="M18.1729 18.3236L16.6431 16.7782L14.5709 14.7152C12.8163 16.4321 10.1343 16.7003 8.08422 15.4132C6.05522 14.1394 5.13249 11.5946 5.90111 9.304C6.62861 7.13583 8.57122 5.6935 10.8735 5.67422C12.6658 5.65922 14.2868 6.51039 15.2906 7.98644C16.6754 10.023 16.4766 12.7273 14.7707 14.5241L18.3133 18.0747C18.3506 18.112 18.3801 18.2127 18.3634 18.2519C18.3433 18.2989 18.2278 18.3358 18.173 18.3236H18.1729ZM14.3014 14.5971C15.2963 13.6639 15.9075 12.4263 15.9341 11.0547C15.9682 9.29228 15.0632 7.63789 13.5644 6.69794C12.1191 5.79144 10.3419 5.68017 8.72588 6.44478C7.40055 7.07189 6.35999 8.35167 6.01844 9.92544C5.67983 11.4856 6.13361 13.1377 7.21233 14.3303C9.06944 16.3834 12.2689 16.5033 14.3014 14.5972V14.5971Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
      <svg class="fnav-icon-account" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Account"><path d="M18.9248 18.8252L5.06173 18.8242C4.9514 18.8242 4.90251 18.7247 4.89185 18.6241C5.01068 15.1657 7.35507 12.3069 10.8648 11.7126C8.91684 11.0286 8.0619 8.817 9.03301 6.99134C9.91007 5.34239 11.9554 4.67278 13.6192 5.58523C14.8117 6.23923 15.5078 7.55372 15.3724 8.90111C15.2403 10.2156 14.4156 11.2364 13.1894 11.7112C16.6343 12.2624 19.0387 15.1935 19.1035 18.579C19.1199 18.6841 19.0962 18.8252 18.9248 18.8252ZM11.9861 11.6267C13.7412 11.6445 15.1553 10.2577 15.1208 8.5265C15.0988 7.41917 14.5122 6.42784 13.5673 5.86811C12.7198 5.36617 11.7115 5.31356 10.7943 5.70906C9.60796 6.22061 8.8409 7.45061 8.91373 8.75489C9.00451 10.3812 10.3435 11.6101 11.9862 11.6267H11.9861ZM18.8419 18.5629C18.7385 14.8503 15.7564 11.8334 12.0082 11.9071C8.24057 11.8907 5.30918 14.8159 5.16807 18.5618L18.8419 18.5629Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Bag, 1 item"><path d="M8.15463 18.9939C6.53363 18.9967 5.27058 17.5904 5.27024 16.0512L5.2688 9.83316L6.79563 9.81422C7.28247 8.38761 8.08463 6.97477 9.27741 6.02738C10.907 4.73311 12.8549 4.64888 14.5483 5.89011C15.8265 6.82699 16.6669 8.35138 17.1976 9.81811L18.728 9.82688L18.7314 16.016C18.7322 17.5641 17.5139 18.9778 15.9205 18.9805L8.15463 18.9939ZM14.7252 9.81738C14.509 8.71372 14.2116 7.75572 13.7489 6.82788C13.4938 6.37377 13.2271 5.94555 12.8341 5.62533C12.3546 5.20816 11.741 5.16799 11.2282 5.54299C10.1624 6.30311 9.45085 8.57166 9.2483 9.82216L14.7252 9.81738ZM8.73835 11.5008C8.89574 9.59749 9.47091 6.83216 10.8821 5.46166C8.47819 6.26083 7.06947 9.06077 6.63619 11.4985L8.73835 11.5008ZM17.3391 11.4891C17.0904 10.0453 16.5815 8.81172 15.8341 7.67033C15.1609 6.68055 14.1732 5.75049 13.0569 5.45644C14.5252 6.76694 15.033 9.63288 15.2306 11.5069L17.3391 11.4891ZM6.35052 11.5007L6.70274 10.0836L5.52869 10.0819L5.53063 11.5094L6.35052 11.5007ZM14.944 11.5054L14.7695 10.0854L9.19669 10.0857C9.1103 10.5613 9.04885 11.0224 9.01713 11.5077L14.944 11.5054ZM18.4634 11.5079L18.4574 10.0822L17.2859 10.0868L17.6197 11.5061L18.4634 11.5079ZM15.9492 18.7362C17.4207 18.6179 18.471 17.4053 18.4684 15.965L18.4608 11.7649L5.52935 11.7668L5.55458 16.1873C5.56263 17.5996 6.75302 18.6682 8.12952 18.7385L15.9492 18.7362Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
    </div>
  </div>`;

  // Light theme: ink-on-white masthead (the artifact only designed it over a
  // dark hero). Same markup, minus the white-on-dark modifiers.
  if (theme === 'light') {
    MASTHEAD = MASTHEAD
      .replace('class="fnav-masthead"', 'class="fnav-masthead fnav-masthead--light"')
      .replace(' fnav-nav-links--hero', '')
      .replace('fnav-mobile-hamburger--hero', 'fnav-mobile-hamburger--compact')
      .replace(' fnav-hero-icon', '')
      .replace('fill="rgb(255,255,255)"', 'fill="rgb(40,40,40)"');
  }

  const COMPACT = `<nav class="fnav-compact-bar" id="fnav-compact-bar" aria-label="Primary, compact">
  <div class="fnav-compact-bar-backdrop" id="fnav-compact-bar-backdrop"></div>
  <div class="fnav-compact-bar-left" id="fnav-compact-bar-left">
    <button class="fnav-mobile-hamburger fnav-mobile-hamburger--compact" id="fnav-hamburger-compact" type="button" aria-label="Open menu">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 17.4165H4V15.9165H20V17.4165ZM20 12.7505H4V11.2505H20V12.7505ZM20 8.0835H4V6.5835H20V8.0835Z" fill="currentColor"/></svg>
    </button>
    <a href="${LAB_HOME}" class="fnav-logo-lockup fnav-logo-lockup--compact" aria-label="FIGS home">
      <svg class="fnav-logo-mark" width="17.5" height="26.7" viewBox="0 0 27 41" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3976 0.00415039C15.4741 0.025623 17.3224 0.380811 18.9152 0.973877C23.5255 2.66954 26.4062 6.70742 26.3868 13.0452V27.1917C26.3597 33.4825 23.4174 37.4784 18.8478 39.1936C17.2433 39.8122 15.0348 40.175 12.9464 40.1223C10.891 40.1418 9.03051 39.7751 7.43762 39.1819C2.86049 37.4959 -0.023103 33.4784 0.00012207 27.1331V12.9749C0.00592853 6.67225 2.94755 2.67541 7.46497 0.983643C9.11981 0.279232 11.3208 -0.040729 13.3976 0.00415039ZM10.8292 17.6829L5.38879 17.6809C5.38879 17.6809 5.45439 22.3137 10.8038 22.425L10.8175 27.886L15.4884 27.9446L15.5148 22.4407L20.9562 22.468C20.9562 22.468 20.4813 18.0849 15.5206 17.7219L15.548 12.2268L10.8546 12.2112L10.8292 17.6829Z" fill="rgb(40,40,40)"/>
      </svg>
      <img src="${ASSETS.wordmark}" alt="FIGS" class="fnav-wordmark fnav-wordmark--compact">
    </a>
    <nav class="fnav-nav-links" aria-label="Primary">
      <a href="#" data-fnav-drawer="women" class="is-active">Women</a>
      <a href="#" data-fnav-drawer="men">Men</a>
      <a href="#" data-fnav-drawer="teams">Teams</a>
      <a href="#" data-fnav-drawer="about">About</a>
    </nav>
  </div>
  <div class="fnav-compact-bar-icons" id="fnav-compact-bar-icons">
    <svg class="fnav-icon-search" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Search"><path d="M18.1729 18.3236L16.6431 16.7782L14.5709 14.7152C12.8163 16.4321 10.1343 16.7003 8.08422 15.4132C6.05522 14.1394 5.13249 11.5946 5.90111 9.304C6.62861 7.13583 8.57122 5.6935 10.8735 5.67422C12.6658 5.65922 14.2868 6.51039 15.2906 7.98644C16.6754 10.023 16.4766 12.7273 14.7707 14.5241L18.3133 18.0747C18.3506 18.112 18.3801 18.2127 18.3634 18.2519C18.3433 18.2989 18.2278 18.3358 18.173 18.3236H18.1729ZM14.3014 14.5971C15.2963 13.6639 15.9075 12.4263 15.9341 11.0547C15.9682 9.29228 15.0632 7.63789 13.5644 6.69794C12.1191 5.79144 10.3419 5.68017 8.72588 6.44478C7.40055 7.07189 6.35999 8.35167 6.01844 9.92544C5.67983 11.4856 6.13361 13.1377 7.21233 14.3303C9.06944 16.3834 12.2689 16.5033 14.3014 14.5972V14.5971Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
    <svg class="fnav-icon-account" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Account"><path d="M18.9248 18.8252L5.06173 18.8242C4.9514 18.8242 4.90251 18.7247 4.89185 18.6241C5.01068 15.1657 7.35507 12.3069 10.8648 11.7126C8.91684 11.0286 8.0619 8.817 9.03301 6.99134C9.91007 5.34239 11.9554 4.67278 13.6192 5.58523C14.8117 6.23923 15.5078 7.55372 15.3724 8.90111C15.2403 10.2156 14.4156 11.2364 13.1894 11.7112C16.6343 12.2624 19.0387 15.1935 19.1035 18.579C19.1199 18.6841 19.0962 18.8252 18.9248 18.8252ZM11.9861 11.6267C13.7412 11.6445 15.1553 10.2577 15.1208 8.5265C15.0988 7.41917 14.5122 6.42784 13.5673 5.86811C12.7198 5.36617 11.7115 5.31356 10.7943 5.70906C9.60796 6.22061 8.8409 7.45061 8.91373 8.75489C9.00451 10.3812 10.3435 11.6101 11.9862 11.6267H11.9861ZM18.8419 18.5629C18.7385 14.8503 15.7564 11.8334 12.0082 11.9071C8.24057 11.8907 5.30918 14.8159 5.16807 18.5618L18.8419 18.5629Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Bag, 1 item"><path d="M8.15463 18.9939C6.53363 18.9967 5.27058 17.5904 5.27024 16.0512L5.2688 9.83316L6.79563 9.81422C7.28247 8.38761 8.08463 6.97477 9.27741 6.02738C10.907 4.73311 12.8549 4.64888 14.5483 5.89011C15.8265 6.82699 16.6669 8.35138 17.1976 9.81811L18.728 9.82688L18.7314 16.016C18.7322 17.5641 17.5139 18.9778 15.9205 18.9805L8.15463 18.9939ZM14.7252 9.81738C14.509 8.71372 14.2116 7.75572 13.7489 6.82788C13.4938 6.37377 13.2271 5.94555 12.8341 5.62533C12.3546 5.20816 11.741 5.16799 11.2282 5.54299C10.1624 6.30311 9.45085 8.57166 9.2483 9.82216L14.7252 9.81738ZM8.73835 11.5008C8.89574 9.59749 9.47091 6.83216 10.8821 5.46166C8.47819 6.26083 7.06947 9.06077 6.63619 11.4985L8.73835 11.5008ZM17.3391 11.4891C17.0904 10.0453 16.5815 8.81172 15.8341 7.67033C15.1609 6.68055 14.1732 5.75049 13.0569 5.45644C14.5252 6.76694 15.033 9.63288 15.2306 11.5069L17.3391 11.4891ZM6.35052 11.5007L6.70274 10.0836L5.52869 10.0819L5.53063 11.5094L6.35052 11.5007ZM14.944 11.5054L14.7695 10.0854L9.19669 10.0857C9.1103 10.5613 9.04885 11.0224 9.01713 11.5077L14.944 11.5054ZM18.4634 11.5079L18.4574 10.0822L17.2859 10.0868L17.6197 11.5061L18.4634 11.5079ZM15.9492 18.7362C17.4207 18.6179 18.471 17.4053 18.4684 15.965L18.4608 11.7649L5.52935 11.7668L5.55458 16.1873C5.56263 17.5996 6.75302 18.6682 8.12952 18.7385L15.9492 18.7362Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
  </div>
</nav>`;

  const DRAWER = `<div class="fnav-nav-drawer-scrim" id="fnav-nav-drawer-scrim"></div>
<div class="fnav-nav-drawer" id="fnav-nav-drawer" data-drawer-panel="women">
  <div class="fnav-nav-drawer-header">
    <a href="${LAB_HOME}" class="fnav-logo-lockup" aria-label="FIGS home">
      <svg class="fnav-logo-mark" width="26.3" height="40" viewBox="0 0 27 41" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3976 0.00415039C15.4741 0.025623 17.3224 0.380811 18.9152 0.973877C23.5255 2.66954 26.4062 6.70742 26.3868 13.0452V27.1917C26.3597 33.4825 23.4174 37.4784 18.8478 39.1936C17.2433 39.8122 15.0348 40.175 12.9464 40.1223C10.891 40.1418 9.03051 39.7751 7.43762 39.1819C2.86049 37.4959 -0.023103 33.4784 0.00012207 27.1331V12.9749C0.00592853 6.67225 2.94755 2.67541 7.46497 0.983643C9.11981 0.279232 11.3208 -0.040729 13.3976 0.00415039ZM10.8292 17.6829L5.38879 17.6809C5.38879 17.6809 5.45439 22.3137 10.8038 22.425L10.8175 27.886L15.4884 27.9446L15.5148 22.4407L20.9562 22.468C20.9562 22.468 20.4813 18.0849 15.5206 17.7219L15.548 12.2268L10.8546 12.2112L10.8292 17.6829Z" fill="rgb(40,40,40)"/>
      </svg>
      <img src="${ASSETS.wordmark}" alt="FIGS" class="fnav-wordmark">
    </a>
    <nav class="fnav-nav-links fnav-nav-drawer-nav" aria-label="Primary">
      <a href="#" data-fnav-drawer="women" class="is-active">Women</a>
      <a href="#" data-fnav-drawer="men">Men</a>
      <a href="#" data-fnav-drawer="teams">Teams</a>
      <a href="#" data-fnav-drawer="about">About</a>
    </nav>
  </div>
  <div class="fnav-nav-drawer-divider"></div>
  <div class="fnav-nav-drawer-body">
    <div class="fnav-nav-drawer-links">
      <div class="fnav-nav-drawer-group">
        <a href="#" class="fnav-nav-drawer-heading">New Arrivals</a>
        <a href="#" class="fnav-nav-drawer-heading">Best Sellers</a>
        <a href="#" class="fnav-nav-drawer-heading fnav-nav-drawer-expandable" data-category="color">Shop by Color<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
      </div>
      <div class="fnav-nav-drawer-group fnav-nav-drawer-group--sub" id="fnav-nav-drawer-primary">
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="scrubs">Scrubs<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="underscrubs">Underscrubs<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="outerwear">Outerwear<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="loungewear">Loungewear<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="lab-coats">Lab Coats<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="shoes">Shoes<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="accessories">Accessories<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
        <a href="#" class="fnav-nav-drawer-sub fnav-nav-drawer-expandable" data-category="jewelry" data-gender="women"><span class="fnav-shimmer-text">Jewelry<sup>New</sup></span><span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
      </div>
      <div class="fnav-nav-drawer-group">
        <a href="${UNIFORM_BUILDER}" class="fnav-nav-drawer-heading">Build Your Uniform</a>
        <a href="${CORE_PLP}" class="fnav-nav-drawer-heading">Core</a>
        <a href="#" class="fnav-nav-drawer-heading fnav-nav-drawer-expandable" data-category="fabric">Shop by Fabric<span class="fnav-expand-icon" aria-hidden="true">
          <svg class="fnav-expand-icon-plus" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.87517 0.027137L5.85482 4.02985C8.07327 4.1791 9.67436 4.72185 9.97286 5.90909L5.83446 5.88874L5.81411 10L4.09091 9.97286L4.07056 5.8616C1.87246 5.81411 0.298507 5.22388 0 4.05699L4.11126 4.07056L4.13161 0L5.87517 0.027137Z" fill="currentColor"/></svg>
          <svg class="fnav-expand-icon-minus" width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.0288808L4.12245 0.0433213L5.87075 0C8.09524 0.158845 9.70068 0.736462 10 2L5.85034 1.97834L4.08163 1.94946C1.87755 1.89892 0.29932 1.27076 0 0.0288808Z" fill="currentColor"/></svg>
        </span></a>
      </div>
      <div class="fnav-nav-drawer-group fnav-nav-drawer-group--sub">
        <a href="#" class="fnav-nav-drawer-sub">Sign up for 15% off</a>
        <a href="#" class="fnav-nav-drawer-sub" data-fnav-account-open>My Account</a>
        <a href="#" class="fnav-nav-drawer-sub">Contact Us</a>
      </div>
    </div>
    <div class="fnav-nav-drawer-images" id="fnav-nav-drawer-images">
      <div class="fnav-nav-drawer-sublevel" id="fnav-nav-drawer-sublevel"></div>
      <div class="fnav-nav-drawer-image-cards" id="fnav-nav-drawer-image-cards">
        <a href="#" class="fnav-nav-drawer-image-card">
          <span class="fnav-nav-drawer-image-caption">
            <span class="fnav-nav-drawer-image-date">11.06.2026</span>
            <span class="fnav-nav-drawer-image-title">The Fall Drop</span>
          </span>
        </a>
        <a href="#" class="fnav-nav-drawer-image-card">
          <span class="fnav-nav-drawer-image-caption">
            <span class="fnav-nav-drawer-image-date">11.06.2026</span>
            <span class="fnav-nav-drawer-image-title">Lorem Ipsum</span>
          </span>
        </a>
        <a href="#" class="fnav-nav-drawer-image-card">
          <span class="fnav-nav-drawer-image-caption">
            <span class="fnav-nav-drawer-image-date">11.06.2026</span>
            <span class="fnav-nav-drawer-image-title">The Fall Drop</span>
          </span>
        </a>
      </div>
    </div>
    <!-- About (node 518:107354) — a flat, static mega-menu rather than the
         Women/Men category drawer: three always-visible heading+links groups
         and a single real photo, with no click-to-expand/image-swap behavior.
         Hidden by default; #nav-drawer.is-about shows this and hides the
         regular .nav-drawer-links/.nav-drawer-images (see setSection in
         script.js). -->
    <div class="fnav-nav-drawer-links fnav-nav-drawer-links-about" id="fnav-nav-drawer-links-about">
      <div class="fnav-nav-drawer-group">
        <a href="#" class="fnav-nav-drawer-heading fnav-nav-drawer-heading-about">About FIGS</a>
        <a href="#" class="fnav-nav-drawer-sub">Our Story</a>
        <a href="#" class="fnav-nav-drawer-sub">Fabric Technology</a>
        <a href="#" class="fnav-nav-drawer-sub">Personalize Your Scrubs</a>
        <a href="#" class="fnav-nav-drawer-sub">Events &amp; Community</a>
      </div>
      <div class="fnav-nav-drawer-group">
        <a href="#" class="fnav-nav-drawer-heading fnav-nav-drawer-heading-about">Our Impact</a>
        <a href="#" class="fnav-nav-drawer-sub">FIGS Impact Hub</a>
        <a href="#" class="fnav-nav-drawer-sub">Advocacy Hub</a>
        <a href="#" class="fnav-nav-drawer-sub">Scrubs That Don’t Suck</a>
      </div>
    </div>
    <div class="fnav-nav-drawer-images fnav-nav-drawer-images-about" id="fnav-nav-drawer-images-about">
      <a href="#" class="fnav-nav-drawer-image-card fnav-nav-drawer-image-card-about">
        <span class="fnav-nav-drawer-image-caption is-about">
          <span class="fnav-nav-drawer-image-title">The Awesome Humans Foundation</span>
        </span>
      </a>
    </div>
    <!-- Teams (node 3616:164842): see teamsMenuHTML. #fnav-nav-drawer.is-teams
         shows it in place of everything else in the body. -->
    ${teamsMenuHTML('desktop')}
  </div>
</div>`;

  const MOBILE = `<div class="fnav-mobile-nav-scrim" id="fnav-mobile-nav-scrim"></div>
<div class="fnav-mobile-nav-drawer" id="fnav-mobile-nav-drawer">
  <div class="fnav-mobile-nav-header">
    <button class="fnav-mobile-nav-close" id="fnav-mobile-nav-close" type="button" aria-label="Close menu">
      <svg class="fnav-mobile-nav-close-x" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.0479 11.1279L18.96 4.21582L19.6621 4.91895L12.751 11.8301L20 19.0791L19.2949 19.7842L12.0459 12.5352L4.70312 19.8809L4 19.1777L11.3438 11.833L4.33398 4.82324L5.03906 4.11914L12.0479 11.1279Z" fill="currentColor"/></svg>
      <svg class="fnav-mobile-nav-close-back" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.4414 4.88281L9.3252 12L16.4414 19.1162L15.5586 20L7.55859 12L15.5586 4L16.4414 4.88281Z" fill="currentColor"/></svg>
    </button>
    <a href="${LAB_HOME}" class="fnav-logo-lockup fnav-logo-lockup--compact" aria-label="FIGS home">
      <svg class="fnav-logo-mark" width="17.5" height="26.7" viewBox="0 0 27 41" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3976 0.00415039C15.4741 0.025623 17.3224 0.380811 18.9152 0.973877C23.5255 2.66954 26.4062 6.70742 26.3868 13.0452V27.1917C26.3597 33.4825 23.4174 37.4784 18.8478 39.1936C17.2433 39.8122 15.0348 40.175 12.9464 40.1223C10.891 40.1418 9.03051 39.7751 7.43762 39.1819C2.86049 37.4959 -0.023103 33.4784 0.00012207 27.1331V12.9749C0.00592853 6.67225 2.94755 2.67541 7.46497 0.983643C9.11981 0.279232 11.3208 -0.040729 13.3976 0.00415039ZM10.8292 17.6829L5.38879 17.6809C5.38879 17.6809 5.45439 22.3137 10.8038 22.425L10.8175 27.886L15.4884 27.9446L15.5148 22.4407L20.9562 22.468C20.9562 22.468 20.4813 18.0849 15.5206 17.7219L15.548 12.2268L10.8546 12.2112L10.8292 17.6829Z" fill="rgb(40,40,40)"/>
      </svg>
      <img src="${ASSETS.wordmark}" alt="FIGS" class="fnav-wordmark fnav-wordmark--compact">
    </a>
    <div class="fnav-mobile-nav-icons">
      <svg class="fnav-icon-search" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Search"><path d="M18.1729 18.3236L16.6431 16.7782L14.5709 14.7152C12.8163 16.4321 10.1343 16.7003 8.08422 15.4132C6.05522 14.1394 5.13249 11.5946 5.90111 9.304C6.62861 7.13583 8.57122 5.6935 10.8735 5.67422C12.6658 5.65922 14.2868 6.51039 15.2906 7.98644C16.6754 10.023 16.4766 12.7273 14.7707 14.5241L18.3133 18.0747C18.3506 18.112 18.3801 18.2127 18.3634 18.2519C18.3433 18.2989 18.2278 18.3358 18.173 18.3236H18.1729ZM14.3014 14.5971C15.2963 13.6639 15.9075 12.4263 15.9341 11.0547C15.9682 9.29228 15.0632 7.63789 13.5644 6.69794C12.1191 5.79144 10.3419 5.68017 8.72588 6.44478C7.40055 7.07189 6.35999 8.35167 6.01844 9.92544C5.67983 11.4856 6.13361 13.1377 7.21233 14.3303C9.06944 16.3834 12.2689 16.5033 14.3014 14.5972V14.5971Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
      <svg class="fnav-icon-account" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Account"><path d="M18.9248 18.8252L5.06173 18.8242C4.9514 18.8242 4.90251 18.7247 4.89185 18.6241C5.01068 15.1657 7.35507 12.3069 10.8648 11.7126C8.91684 11.0286 8.0619 8.817 9.03301 6.99134C9.91007 5.34239 11.9554 4.67278 13.6192 5.58523C14.8117 6.23923 15.5078 7.55372 15.3724 8.90111C15.2403 10.2156 14.4156 11.2364 13.1894 11.7112C16.6343 12.2624 19.0387 15.1935 19.1035 18.579C19.1199 18.6841 19.0962 18.8252 18.9248 18.8252ZM11.9861 11.6267C13.7412 11.6445 15.1553 10.2577 15.1208 8.5265C15.0988 7.41917 14.5122 6.42784 13.5673 5.86811C12.7198 5.36617 11.7115 5.31356 10.7943 5.70906C9.60796 6.22061 8.8409 7.45061 8.91373 8.75489C9.00451 10.3812 10.3435 11.6101 11.9862 11.6267H11.9861ZM18.8419 18.5629C18.7385 14.8503 15.7564 11.8334 12.0082 11.9071C8.24057 11.8907 5.30918 14.8159 5.16807 18.5618L18.8419 18.5629Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Bag, 1 item"><path d="M8.15463 18.9939C6.53363 18.9967 5.27058 17.5904 5.27024 16.0512L5.2688 9.83316L6.79563 9.81422C7.28247 8.38761 8.08463 6.97477 9.27741 6.02738C10.907 4.73311 12.8549 4.64888 14.5483 5.89011C15.8265 6.82699 16.6669 8.35138 17.1976 9.81811L18.728 9.82688L18.7314 16.016C18.7322 17.5641 17.5139 18.9778 15.9205 18.9805L8.15463 18.9939ZM14.7252 9.81738C14.509 8.71372 14.2116 7.75572 13.7489 6.82788C13.4938 6.37377 13.2271 5.94555 12.8341 5.62533C12.3546 5.20816 11.741 5.16799 11.2282 5.54299C10.1624 6.30311 9.45085 8.57166 9.2483 9.82216L14.7252 9.81738ZM8.73835 11.5008C8.89574 9.59749 9.47091 6.83216 10.8821 5.46166C8.47819 6.26083 7.06947 9.06077 6.63619 11.4985L8.73835 11.5008ZM17.3391 11.4891C17.0904 10.0453 16.5815 8.81172 15.8341 7.67033C15.1609 6.68055 14.1732 5.75049 13.0569 5.45644C14.5252 6.76694 15.033 9.63288 15.2306 11.5069L17.3391 11.4891ZM6.35052 11.5007L6.70274 10.0836L5.52869 10.0819L5.53063 11.5094L6.35052 11.5007ZM14.944 11.5054L14.7695 10.0854L9.19669 10.0857C9.1103 10.5613 9.04885 11.0224 9.01713 11.5077L14.944 11.5054ZM18.4634 11.5079L18.4574 10.0822L17.2859 10.0868L17.6197 11.5061L18.4634 11.5079ZM15.9492 18.7362C17.4207 18.6179 18.471 17.4053 18.4684 15.965L18.4608 11.7649L5.52935 11.7668L5.55458 16.1873C5.56263 17.5996 6.75302 18.6682 8.12952 18.7385L15.9492 18.7362Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
    </div>
  </div>

  <nav class="fnav-mobile-nav-tabs fnav-nav-links" aria-label="Primary">
    <a href="#" data-fnav-drawer="women" class="is-active">Women</a>
    <a href="#" data-fnav-drawer="men">Men</a>
    <a href="#" data-fnav-drawer="teams">Teams</a>
    <a href="#" data-fnav-drawer="about">About</a>
  </nav>

  <div class="fnav-mobile-nav-body">
    <div class="fnav-mobile-nav-highlights">
      <a href="#" class="fnav-nav-drawer-heading">New Arrivals</a>
      <a href="#" class="fnav-nav-drawer-heading">Best Sellers</a>
      <a href="#" class="fnav-nav-drawer-heading" id="fnav-mobile-shop-by-color">Shop by Color</a>
    </div>

    <div class="fnav-mobile-nav-chips-wrap">
      <div class="fnav-mobile-nav-chips" id="fnav-mobile-nav-chips">
        <button class="fnav-mobile-chip is-active" type="button" data-category="scrubs">Scrubs</button>
        <button class="fnav-mobile-chip" type="button" data-category="underscrubs">Underscrubs</button>
        <button class="fnav-mobile-chip" type="button" data-category="outerwear">Outerwear</button>
        <button class="fnav-mobile-chip" type="button" data-category="loungewear">Loungewear</button>
        <button class="fnav-mobile-chip" type="button" data-category="lab-coats">Lab Coats</button>
        <button class="fnav-mobile-chip" type="button" data-category="shoes">Shoes</button>
        <button class="fnav-mobile-chip" type="button" data-category="accessories">Accessories</button>
        <button class="fnav-mobile-chip" type="button" data-category="jewelry" data-gender="women"><span class="fnav-shimmer-text">Jewelry<sup>New</sup></span></button>
      </div>
      <!-- About's mobile sub-tabs (node 1138:29565) — a separate static row
           instead of rebuilding #mobile-nav-chips, so entering/exiting About
           never destroys and re-creates the normal chips' click listeners. -->
      <div class="fnav-mobile-nav-chips fnav-mobile-nav-chips-about" id="fnav-mobile-nav-chips-about">
        <button class="fnav-mobile-chip is-active" type="button" data-about="about">About FIGS</button>
        <button class="fnav-mobile-chip" type="button" data-about="impact">Our Impact</button>
      </div>
    </div>

    <div class="fnav-mobile-nav-sublevel" id="fnav-mobile-nav-sublevel"></div>

    <div class="fnav-mobile-nav-carousel" id="fnav-mobile-nav-carousel"></div>

    <div class="fnav-mobile-nav-footer">
      <div class="fnav-mobile-nav-highlights">
        <a href="${UNIFORM_BUILDER}" class="fnav-nav-drawer-heading">Build Your Uniform</a>
        <a href="${CORE_PLP}" class="fnav-nav-drawer-heading">Core</a>
        <a href="#" class="fnav-nav-drawer-heading" id="fnav-mobile-shop-by-fabric">Shop by Fabric</a>
      </div>

      <div class="fnav-mobile-nav-utility">
        <a href="#" class="fnav-nav-drawer-sub">Sign up for 15% off</a>
        <a href="#" class="fnav-nav-drawer-sub" data-fnav-account-open>My Account</a>
        <a href="#" class="fnav-nav-drawer-sub">Students</a>
        <a href="#" class="fnav-nav-drawer-sub">Contact Us</a>
        <span class="fnav-nav-drawer-sub fnav-mobile-nav-locale fnav-js-open-country-drawer">US | English <img src="${asset("flag-us.png")}" alt="" class="fnav-locale-flag"></span>
        <a href="#" class="fnav-nav-drawer-sub">Find a Hub</a>
        <a href="#" class="fnav-nav-drawer-sub">Get the App</a>
      </div>
    </div>

    <!-- Teams (node 3609:158560 / 3616:164585): #fnav-mobile-nav-drawer.is-teams-page
         shows it in place of the rest of the body. -->
    ${teamsMenuHTML('mobile')}
  </div>
</div>`;

  if (promo) promo.replaceWith(fragment(PROMO));
  masthead.replaceWith(fragment(MASTHEAD));
  const EXTRAS = `<section class="fnav-results-page" id="fnav-results-page" hidden>
  <div class="fnav-results-banner">
    <div class="fnav-results-banner-scrim"></div>
    <div class="fnav-results-banner-content">
      <span class="fnav-hero-preheader">Preheader</span>
      <h2 class="fnav-hero-headline">get figs</h2>
      <p class="fnav-hero-body">OUR MISSION is to celebrate, empower and serve those who serve others. The AWESOME HUMANS who are doing the most important work in the world – CARING for patients, SAVING LIVES, advancing medicine, and SHOWING UP every single day.</p>
      <div class="fnav-hero-ctas">
        <a href="#" class="fnav-hero-btn">Shop Now</a>
        <a href="#" class="fnav-hero-btn">Watch Video</a>
      </div>
    </div>
  </div>

  <div class="fnav-results-tagline">
    <h1 class="fnav-results-tagline-text" id="fnav-results-tagline-text">Search Results for ‘Rafaela’</h1>
  </div>

  <div class="fnav-results-filter-bar">
    <div class="fnav-results-filters">
      <span class="fnav-results-filter-pill fnav-results-filter-main">Filter &amp; Sort</span>
      <span class="fnav-results-filter-pill">Colors</span>
      <span class="fnav-results-filter-pill">Size</span>
      <span class="fnav-results-filter-pill">Style</span>
    </div>
    <div class="fnav-results-toggle">
      <span class="fnav-results-toggle-option is-active">Model</span>
      <span class="fnav-results-toggle-option">Product</span>
    </div>
    <span class="fnav-results-product-count" id="fnav-results-product-count">5 PRODUCTS</span>
  </div>

  <div class="fnav-results-product-grid" id="fnav-results-product-grid"></div>

  <div class="fnav-results-split-grid">
    <span class="fnav-results-split-grid-title">Limited Edition</span>
    <span class="fnav-results-split-grid-sub">Here for a good time, not a long time. Grab your favorites before they’re gone.</span>
  </div>

  <div class="fnav-results-product-grid" id="fnav-results-product-grid-2"></div>

  <div class="fnav-results-featured-gallery">
    <div class="fnav-results-featured-main" style="background-image: url('${asset("results-featured-main.jpg")}')"></div>
    <div class="fnav-results-featured-sub-grid">
      <div class="fnav-results-featured-sub" style="background-image: url('${asset("results-featured-sub-1.jpg")}')"></div>
      <div class="fnav-results-featured-sub" style="background-image: url('${asset("results-featured-sub-2.jpg")}')"></div>
      <div class="fnav-results-featured-sub" style="background-image: url('${asset("results-featured-sub-3.jpg")}')"></div>
      <div class="fnav-results-featured-sub" style="background-image: url('${asset("results-featured-sub-4.jpg")}')"></div>
    </div>
  </div>

  <div class="fnav-results-carousel-section">
    <div class="fnav-results-carousel-header">
      <div class="fnav-results-carousel-heading">
        <span class="fnav-hero-preheader fnav-results-carousel-preheader">Preheader</span>
        <span class="fnav-results-carousel-title">Marie, Picks For You</span>
        <span class="fnav-results-carousel-subtitle">Title Lorem</span>
      </div>
      <div class="fnav-results-toggle">
        <span class="fnav-results-toggle-option is-active">Women</span>
        <span class="fnav-results-toggle-option">Men</span>
      </div>
    </div>
    <div class="fnav-results-carousel-track" id="fnav-results-carousel-track"></div>
  </div>

  ${siteFooter()}
</section>
<div class="fnav-search-drawer-scrim" id="fnav-search-drawer-scrim"></div>
<div class="fnav-search-drawer" id="fnav-search-drawer">
  <div class="fnav-search-drawer-top">
    <div class="fnav-search-bar">
      <div class="fnav-search-bar-left">
        <svg class="fnav-search-bar-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M18.1729 18.3236L16.6431 16.7782L14.5709 14.7152C12.8163 16.4321 10.1343 16.7003 8.08422 15.4132C6.05522 14.1394 5.13249 11.5946 5.90111 9.304C6.62861 7.13583 8.57122 5.6935 10.8735 5.67422C12.6658 5.65922 14.2868 6.51039 15.2906 7.98644C16.6754 10.023 16.4766 12.7273 14.7707 14.5241L18.3133 18.0747C18.3506 18.112 18.3801 18.2127 18.3634 18.2519C18.3433 18.2989 18.2278 18.3358 18.173 18.3236H18.1729ZM14.3014 14.5971C15.2963 13.6639 15.9075 12.4263 15.9341 11.0547C15.9682 9.29228 15.0632 7.63789 13.5644 6.69794C12.1191 5.79144 10.3419 5.68017 8.72588 6.44478C7.40055 7.07189 6.35999 8.35167 6.01844 9.92544C5.67983 11.4856 6.13361 13.1377 7.21233 14.3303C9.06944 16.3834 12.2689 16.5033 14.3014 14.5972V14.5971Z" fill="currentColor" stroke="currentColor" stroke-width="0.85" stroke-miterlimit="10"/></svg>
        <input type="text" class="fnav-search-bar-input" id="fnav-search-input" placeholder="Search" autocomplete="off" spellcheck="false">
      </div>
      <div class="fnav-search-bar-right">
        <button type="button" class="fnav-search-clear" id="fnav-search-clear" hidden>Clear</button>
        <button type="button" class="fnav-search-drawer-close" id="fnav-search-drawer-close" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.0479 11.1279L18.96 4.21582L19.6621 4.91895L12.751 11.8301L20 19.0791L19.2949 19.7842L12.0459 12.5352L4.70312 19.8809L4 19.1777L11.3438 11.833L4.33398 4.82324L5.03906 4.11914L12.0479 11.1279Z" fill="currentColor"/></svg>
        </button>
      </div>
    </div>
    <div class="fnav-search-drawer-divider"></div>
  </div>

  <p class="fnav-search-error-message" id="fnav-search-error-message" hidden>Please try a different search term.</p>

  <span class="fnav-search-drawer-heading" id="fnav-search-drawer-heading">Trending Searches</span>

  <div class="fnav-search-products" id="fnav-search-products"></div>

  <div class="fnav-search-help" id="fnav-search-help" hidden>
    <span class="fnav-search-help-title">Need Help?</span>
    <a href="#" class="fnav-nav-drawer-sub">Contact Us</a>
    <a href="#" class="fnav-nav-drawer-sub">Help Center</a>
  </div>
</div>
<div class="fnav-country-drawer-scrim" id="fnav-country-drawer-scrim"></div>
<div class="fnav-country-drawer" id="fnav-country-drawer">
  <div class="fnav-country-drawer-header">
    <span class="fnav-country-drawer-title">
      Choose your location
      <img src="${asset("flag-us.png")}" alt="" class="fnav-locale-flag">
    </span>
    <button class="fnav-country-drawer-close" id="fnav-country-drawer-close" type="button" aria-label="Close">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.0479 11.1279L18.96 4.21582L19.6621 4.91895L12.751 11.8301L20 19.0791L19.2949 19.7842L12.0459 12.5352L4.70312 19.8809L4 19.1777L11.3438 11.833L4.33398 4.82324L5.03906 4.11914L12.0479 11.1279Z" fill="currentColor"/></svg>
    </button>
  </div>

  <div class="fnav-country-drawer-form">
    <label class="fnav-country-drawer-field">
      <span class="fnav-country-drawer-label">Country / region <span class="fnav-country-drawer-asterisk">*</span></span>
      <span class="fnav-country-drawer-select-wrap">
        <select class="fnav-country-drawer-select">
          <option>United States</option>
          <option>Canada</option>
          <option>United Kingdom</option>
          <option>Australia</option>
        </select>
        <svg class="fnav-country-drawer-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 7L0 1.08L1.08 0L6 4.84L10.92 0L12 1.08L6 7Z" fill="currentColor"/></svg>
      </span>
    </label>
    <label class="fnav-country-drawer-field">
      <span class="fnav-country-drawer-label">Language <span class="fnav-country-drawer-asterisk">*</span></span>
      <span class="fnav-country-drawer-select-wrap">
        <select class="fnav-country-drawer-select">
          <option>English</option>
          <option>French</option>
          <option>Spanish</option>
        </select>
        <svg class="fnav-country-drawer-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 7L0 1.08L1.08 0L6 4.84L10.92 0L12 1.08L6 7Z" fill="currentColor"/></svg>
      </span>
    </label>
  </div>

  <button class="fnav-account-drawer-submit" id="fnav-country-drawer-save" type="button">Save</button>

  <div class="fnav-country-drawer-terms">
    <a href="#">FIGS’ Privacy and Cookie Policy</a>
    <a href="#">Global-e’s Privacy Policy</a>
  </div>
</div>`;

  document.body.append(fragment(COMPACT), fragment(DRAWER), fragment(MOBILE), fragment(EXTRAS));
  mountFigsFooter(footer); // the placeholder, or the page's own footer, becomes the site footer

  // Mobile category chips go 400 → 700 when selected, which widened the
  // active chip by up to ~3px and nudged every chip after it. Wrap each
  // chip's label so CSS can reserve its bold width up front (see
  // .fnav-mobile-chip__label) — selecting a chip then changes weight only,
  // never layout. Any <sup> ("New") stays outside the wrapper.
  document.querySelectorAll('.fnav-mobile-chip').forEach((chip) => {
    // The label text may sit directly in the chip or inside a wrapper such as
    // Jewelry's .fnav-shimmer-text — take the first real text node either way,
    // skipping the <sup> badge.
    const walker = document.createTreeWalker(chip, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (n.textContent.trim() && !n.parentElement.closest('sup') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
    });
    const text = walker.nextNode();
    if (!text) return;
    const label = document.createElement('span');
    label.className = 'fnav-mobile-chip__label';
    label.dataset.label = text.textContent.trim();
    text.replaceWith(label);
    label.append(text);
  });

  // Shared across both IIFEs below: which gender's assortment the nav drawers
  // (desktop hover + mobile) currently show. Switching re-syncs every "Women" /
  // "Men" link's bold state and tells the drawer content to reset to default.
  let currentGender = 'women';

  // Every link with a lab page behind it (LAB, above) points there for the
  // gender on screen: the headings and highlights, the masthead's Women / Men
  // and About's Personalize Your Scrubs. Once mounted, and on each gender
  // change. Sign up for 15% off opens the account drawer's Sign Up in place.
  function syncLabLinks() {
    const headings = HEADING_HREFS[currentGender];
    document.querySelectorAll('#fnav-nav-drawer .fnav-nav-drawer-heading, #fnav-mobile-nav-drawer .fnav-nav-drawer-heading').forEach((a) => {
      const href = headings[a.textContent.trim()];
      if (href) a.href = href;
    });
    document.querySelectorAll('a[data-fnav-drawer="women"], a[data-fnav-drawer="men"]').forEach((a) => {
      if (!a.closest('.fnav-mobile-nav-tabs')) a.href = GENDER_HREFS[a.dataset.fnavDrawer];
    });
    document.querySelectorAll('#fnav-nav-drawer-links-about a').forEach((a) => {
      const href = ABOUT_HREFS[a.textContent.trim()];
      if (href) a.href = href;
    });
  }
  syncLabLinks();
  document.querySelectorAll('#fnav-nav-drawer .fnav-nav-drawer-sub, #fnav-mobile-nav-drawer .fnav-nav-drawer-sub').forEach((a) => {
    if (a.textContent.trim() !== 'Sign up for 15% off') return;
    a.addEventListener('click', (e) => { e.preventDefault(); openFigsAccount('signup'); });
  });

  function setGender(gender) {
    if (gender === currentGender) return;
    currentGender = gender;
    syncLabLinks();

    const desktopDrawer = document.getElementById('fnav-nav-drawer');
    const mobileDrawerEl = document.getElementById('fnav-mobile-nav-drawer');
    desktopDrawer.classList.toggle('is-men', gender === 'men');
    desktopDrawer.setAttribute('data-drawer-panel', gender);
    mobileDrawerEl.classList.toggle('is-men', gender === 'men');

    document.querySelectorAll('[data-fnav-drawer]').forEach((link) => {
      link.classList.toggle('is-active', link.dataset.fnavDrawer === gender);
    });

    desktopDrawer.dispatchEvent(new CustomEvent('fnav-nav-drawer:gender-change', { detail: { gender } }));
  }

  // About (node 518:107354) and Teams (3616:164842) are peer sections
  // alongside Women/Men, but structurally nothing like them — flat static
  // menus, not a gendered product catalog — so they're kept entirely separate
  // from currentGender/setGender rather than taught to the gender machinery
  // as more values. `staticSection` is the one showing ('about' | 'teams'),
  // or null; the last-viewed gender underneath is left untouched so
  // Women/Men pick back up where they were.
  const STATIC_SECTIONS = ['about', 'teams'];
  let staticSection = null;

  function markStaticSection(section) {
    const desktopDrawer = document.getElementById('fnav-nav-drawer');
    STATIC_SECTIONS.forEach((s) => desktopDrawer.classList.toggle(`is-${s}`, s === section));
  }

  function setSection(section) {
    const desktopDrawer = document.getElementById('fnav-nav-drawer');
    if (STATIC_SECTIONS.includes(section)) {
      if (staticSection === section) return;
      staticSection = section;
      markStaticSection(section);
      document.querySelectorAll('[data-fnav-drawer]').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.fnavDrawer === section);
      });
      desktopDrawer.dispatchEvent(new CustomEvent('fnav-nav-drawer:section-change', { detail: { section } }));
      return;
    }
    if (staticSection) {
      staticSection = null;
      markStaticSection(null);
      // setGender no-ops when `section` already equals currentGender (e.g.
      // About was entered from Women and we're returning to Women) — it isn't
      // expecting About to have hijacked the is-active state in the meantime,
      // so that resync has to happen here rather than being left to it.
      document.querySelectorAll('[data-fnav-drawer]').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.fnavDrawer === section);
      });
      desktopDrawer.dispatchEvent(new CustomEvent('fnav-nav-drawer:section-change', { detail: { section } }));
    }
    setGender(section);
  }

  // Teams' access block follows sign-in: the logged-out frames for a guest,
  // the Team Admin State once signed in (on this tab, or another one).
  // Sign in to FIGS opens the account drawer's Log In.
  const teamsPanels = document.querySelectorAll('[data-fnav-teams]');
  function syncTeams() {
    const admin = isFigsSignedIn();
    teamsPanels.forEach((panel) => panel.classList.toggle('is-admin', admin));
  }
  syncTeams();
  document.addEventListener('figs-account:change', syncTeams);
  // figs-account.js re-reads the session on the same event, after this runs
  addEventListener('storage', (e) => { if (e.key === 'figs-lab:account') setTimeout(syncTeams, 0); });
  teamsPanels.forEach((panel) => panel.addEventListener('click', (e) => {
    if (!e.target.closest('[data-fnav-teams-signin]')) return;
    e.preventDefault();
    openFigsAccount('login');
  }));

  // Locks page scroll while a full-height overlay (the desktop nav drawer, the
  // mobile drawer, or the account drawer) is open. Shared at top level rather
  // than duplicated per IIFE since none of them capture anything closure-local.
  // Locking overflow on <body> alone doesn't reliably stop the document from
  // scrolling in every browser — the root scroller can be <html> — so both get
  // it, matching the standard cross-browser scroll-lock pattern.
  let scrollLockPadding = false;
  function lockBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      scrollLockPadding = true;
    }
  }
  function unlockBodyScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (scrollLockPadding) {
      document.body.style.paddingRight = '';
      scrollLockPadding = false;
    }
  }

  // The account drawer and search drawer are both right-side panels pinned to
  // the exact same spot — having both open at once would just stack two
  // identical-looking white panels on top of each other. Each one calls this
  // with its own name before opening so the other closes first.
  function closeOtherOverlays(except) {
    document.dispatchEvent(new CustomEvent('overlay:open', { detail: { except } }));
  }

  (() => {
    const masthead = document.getElementById('fnav-masthead');
    const compactBar = document.getElementById('fnav-compact-bar');
    const links = compactBar.querySelectorAll('.fnav-nav-links a');
    const EASE = [0.4, 0, 0.2, 1];

    let compactVisible = false;
    let barAnim = null;
    let linkAnims = [];

    // Shared "accordion in" stagger: slide from -24px + fade in, 60ms apart, starting
    // 100ms after the trigger. Used by the compact bar's own links and (see below)
    // the drawer's logo/nav when opened from the compact bar.
    function accordionIn(elements, baseDelay = 0.1, stagger = 0.06, duration = 0.4) {
      const anims = [];
      const M = Motion;
      if (!M || !M.animate || !elements.length) {
        elements.forEach((el) => {
          el.style.opacity = '';
          el.style.transform = '';
        });
        return anims;
      }
      const staggerFn = M.stagger ? M.stagger(stagger) : (i) => i * stagger;
      elements.forEach((el, i) => {
        el.style.transform = 'translateX(-24px)';
        el.style.opacity = '0';
        const delay = typeof staggerFn === 'function' ? staggerFn(i, { total: elements.length }) : 0;
        anims.push(
          M.animate(
            el,
            { transform: ['translateX(-24px)', 'translateX(0px)'], opacity: [0, 1] },
            { duration, delay: baseDelay + delay, ease: EASE }
          )
        );
        setTimeout(() => {
          el.style.transform = 'translateX(0px)';
          el.style.opacity = '1';
        }, (baseDelay + delay + duration) * 1000);
      });
      return anims;
    }

    // Reverse of accordionIn: fades/slides elements out to the left. The stagger runs
    // back-to-front (last element first) so the group folds shut toward its anchor.
    function accordionOut(elements, stagger = 0.05, duration = 0.25) {
      const anims = [];
      const M = Motion;
      const n = elements.length;
      if (!M || !M.animate || !n) {
        elements.forEach((el) => {
          el.style.opacity = '0';
          el.style.transform = 'translateX(-24px)';
        });
        return { anims, totalDuration: 0 };
      }
      let maxFinish = 0;
      elements.forEach((el, i) => {
        const delay = (n - 1 - i) * stagger;
        maxFinish = Math.max(maxFinish, delay + duration);
        anims.push(
          M.animate(
            el,
            { transform: ['translateX(0px)', 'translateX(-24px)'], opacity: [1, 0] },
            { duration, delay, ease: EASE }
          )
        );
        setTimeout(() => {
          el.style.transform = 'translateX(-24px)';
          el.style.opacity = '0';
        }, (delay + duration) * 1000);
      });
      return { anims, totalDuration: maxFinish };
    }

    function animateCompactBar(visible) {
      if (barAnim && barAnim.cancel) barAnim.cancel();

      const M = Motion;
      compactBar.style.pointerEvents = visible ? 'auto' : 'none';

      const fromY = visible ? '-100%' : '0%';
      const toY = visible ? '0%' : '-100%';
      const fromOp = visible ? 0 : 1;
      const toOp = visible ? 1 : 0;

      // Motion's animate() doesn't reliably keep the end keyframe applied once the
      // animation finishes in this environment, so the resting inline style is set
      // in parallel — the animation supplies the transition, the inline style supplies
      // the permanent result.
      compactBar.style.transform = `translateY(${toY})`;
      compactBar.style.opacity = String(toOp);

      if (M && M.animate) {
        barAnim = M.animate(
          compactBar,
          { transform: [`translateY(${fromY})`, `translateY(${toY})`], opacity: [fromOp, toOp] },
          { duration: 0.4, ease: EASE }
        );
      } else {
        compactBar.style.transition = 'transform .4s cubic-bezier(.4,0,.2,1), opacity .4s cubic-bezier(.4,0,.2,1)';
      }

      linkAnims.forEach((a) => a && a.cancel && a.cancel());
      linkAnims = visible ? accordionIn(Array.from(links)) : [];
      if (!visible) {
        links.forEach((link) => {
          link.style.opacity = '';
          link.style.transform = '';
        });
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = !entries[0].isIntersecting;
        if (visible !== compactVisible) {
          compactVisible = visible;
          animateCompactBar(visible);
        }
      },
      { threshold: 0 }
    );

    observer.observe(masthead);

    // Nav drawer (hover megamenu) — the drawer contains its own logo/wordmark/nav,
    // so the real header's left-group hides while the drawer stands in for it.
    const scrim = document.getElementById('fnav-nav-drawer-scrim');
    const drawer = document.getElementById('fnav-nav-drawer');
    const mastheadLeft = document.getElementById('fnav-masthead-left');
    const compactBarLeft = document.getElementById('fnav-compact-bar-left');
    const compactBarBackdrop = document.getElementById('fnav-compact-bar-backdrop');
    const compactBarIcons = document.getElementById('fnav-compact-bar-icons');
    const compactBarIconImgs = compactBarIcons.querySelectorAll('svg');
    const compactBarLogoMark = compactBarLeft.querySelector('.fnav-logo-mark');
    const compactBarWordmark = compactBarLeft.querySelector('.fnav-wordmark');
    const drawerLogo = drawer.querySelector('.fnav-nav-drawer-header .fnav-logo-lockup');
    const drawerNavLinks = drawer.querySelectorAll('.fnav-nav-drawer-header .fnav-nav-links a');
    // Excludes .mobile-nav-tabs — those three share the same data-drawer
    // attribute for styling/setSection's sake, but are click-only (mobile has
    // no hover). Without this, hovering/clicking one fires this desktop
    // openDrawer too: drawerOpen (desktop) flips true, the click then bubbles
    // to document and gets read as an outside-click on #nav-drawer (the mobile
    // tab isn't inside it), which calls closeDrawer and resets everything —
    // confirmed via a real click simulation (which, unlike .click(), fires a
    // genuine mouseenter first) showing setSection('about') get silently
    // undone immediately after.
    const triggers = Array.from(
      document.querySelectorAll('[data-fnav-drawer="women"], [data-fnav-drawer="men"], [data-fnav-drawer="about"], [data-fnav-drawer="teams"]')
    ).filter((el) => !el.closest('.fnav-mobile-nav-tabs'));

    let drawerOpen = false;
    let drawerAnim = null;
    let scrimAnim = null;
    let backdropAnim = null;
    let headerAnims = [];
    let exitAnims = [];
    let revealTimer = null;
    let closeTimer = null;
    // Which header row the currently-open drawer was triggered from. Set once, at
    // open time, from the trigger element itself — deliberately NOT derived from
    // `compactVisible`, which is updated asynchronously by an IntersectionObserver
    // and can still read stale right after a fast scroll-to-top, pointing the
    // drawer at the compact bar's position (top: 0) instead of the fnav-hero fnav-masthead's.
    let drawerFromCompact = false;

    // COS's own desktop nav drawer (cos.com) has no slide — the panel just appears
    // in place. We match that with a fast opacity-only fade instead of a translateX.
    const DRAWER_FADE_DURATION = 0.15;

    // The hand-off from the compact bar to the drawer: the bar's backdrop slides up
    // and out while the drawer settles down into place, instead of an instant swap.
    const BAR_SWAP_DURATION = 0.3;

    function fadeScrim(top, toOp, duration) {
      const fromOp = parseFloat(getComputedStyle(scrim).opacity) || 0;
      if (scrimAnim && scrimAnim.cancel) scrimAnim.cancel();
      scrim.style.top = `${top}px`;
      scrim.style.pointerEvents = toOp ? 'auto' : 'none';
      scrim.style.opacity = String(toOp);
      const M = Motion;
      if (M && M.animate) {
        scrimAnim = M.animate(scrim, { opacity: [fromOp, toOp] }, { duration, ease: EASE });
      }
    }

    // The drawer and scrim always start at the top of the viewport, so they
    // cover the promo bar too. When opened from the full masthead (which sits
    // below the promo bar), the drawer's own top padding grows by the
    // masthead's offset so its logo/nav still line up exactly with the
    // masthead's.
    function revealDrawer(top) {
      drawer.style.top = '0px';
      drawer.style.paddingTop = '';
      const basePadding = parseFloat(getComputedStyle(drawer).paddingTop) || 0;
      drawer.style.paddingTop = `${basePadding + top}px`;
      drawer.style.pointerEvents = 'auto';
      if (drawerAnim && drawerAnim.cancel) drawerAnim.cancel();
      const M = Motion;

      headerAnims.forEach((a) => a && a.cancel && a.cancel());
      if (drawerFromCompact) {
        // The bar itself only disappears once its logo+links have fully collapsed —
        // doing it earlier left a beat where the bar was already gone but the links
        // were still visibly sliding in on top of the scrim.
        compactBarIconImgs.forEach((img) => img.classList.add('fnav-hero-icon'));

        if (backdropAnim && backdropAnim.cancel) backdropAnim.cancel();
        compactBarBackdrop.style.transform = 'translateY(-100%)';
        if (M && M.animate) {
          backdropAnim = M.animate(
            compactBarBackdrop,
            { transform: ['translateY(0%)', 'translateY(-100%)'] },
            { duration: BAR_SWAP_DURATION, ease: EASE }
          );
        }

        drawer.style.opacity = '1';
        drawer.style.transform = 'translateY(0px)';
        if (M && M.animate) {
          drawerAnim = M.animate(
            drawer,
            { opacity: [0, 1], transform: ['translateY(-24px)', 'translateY(0px)'] },
            { duration: BAR_SWAP_DURATION, ease: EASE }
          );
        }

        headerAnims = accordionIn([drawerLogo, ...drawerNavLinks]);
      } else {
        drawer.style.opacity = '1';
        drawer.style.transform = 'none';
        if (M && M.animate) {
          drawerAnim = M.animate(drawer, { opacity: [0, 1] }, { duration: DRAWER_FADE_DURATION, ease: EASE });
        }
        [drawerLogo, ...drawerNavLinks].forEach((el) => {
          el.style.opacity = '';
          el.style.transform = '';
        });
      }
    }

    function openDrawer(trigger) {
      clearTimeout(revealTimer);
      // Runs first regardless of whether the drawer is already open — hovering
      // from "Women" straight across to "Men" (or "About") should swap the
      // panel's content in place rather than require a close-then-reopen round trip.
      setSection(trigger.dataset.fnavDrawer);
      if (drawerOpen) return;
      drawerOpen = true;
      lockBodyScroll();

      const fromCompact = trigger.closest('#fnav-compact-bar') !== null;
      drawerFromCompact = fromCompact;
      const bar = fromCompact ? compactBar : masthead;
      const left = fromCompact ? compactBarLeft : mastheadLeft;
      const top = Math.round(bar.getBoundingClientRect().top);

      if (exitAnims.length) exitAnims.forEach((a) => a && a.cancel && a.cancel());

      if (fromCompact) {
        // Sequence: the bar's own logo+links accordion away to the left first; only
        // once that's fully collapsed does the bar disappear, icons flip white, and
        // the drawer fade in with its own logo/nav accordioning back in.
        left.style.pointerEvents = 'none';

        const { anims, totalDuration } = accordionOut([compactBarLogoMark, compactBarWordmark, ...links]);
        exitAnims = anims;
        fadeScrim(0, 1, Math.max(totalDuration, 0.3));

        revealTimer = setTimeout(() => revealDrawer(top), totalDuration * 1000);
      } else {
        left.style.opacity = '0';
        left.style.pointerEvents = 'none';
        fadeScrim(0, 1, DRAWER_FADE_DURATION);
        revealDrawer(top);
      }
    }

    function closeDrawer() {
      clearTimeout(revealTimer);
      if (!drawerOpen) return;
      drawerOpen = false;
      unlockBodyScroll();
      drawer.dispatchEvent(new Event('fnav-nav-drawer:close'));

      // An outside click closes the drawer directly, bypassing setSection —
      // if About or Teams was showing, resync the tab row back to
      // currentGender here too, same reasoning as closeMobileDrawer's
      // equivalent block.
      if (staticSection) {
        staticSection = null;
        markStaticSection(null);
        document.querySelectorAll('[data-fnav-drawer]').forEach((link) => {
          link.classList.toggle('is-active', link.dataset.fnavDrawer === currentGender);
        });
      }

      drawer.style.pointerEvents = 'none';
      mastheadLeft.style.opacity = '';
      mastheadLeft.style.pointerEvents = '';
      compactBarLeft.style.opacity = '';
      compactBarLeft.style.pointerEvents = '';

      headerAnims.forEach((a) => a && a.cancel && a.cancel());
      headerAnims = [];
      [drawerLogo, ...drawerNavLinks].forEach((el) => {
        el.style.opacity = '';
        el.style.transform = '';
      });

      if (drawerAnim && drawerAnim.cancel) drawerAnim.cancel();
      const M = Motion;

      drawer.style.opacity = '0';
      if (drawerFromCompact) {
        drawer.style.transform = 'translateY(-24px)';
        if (M && M.animate) {
          drawerAnim = M.animate(
            drawer,
            { opacity: [1, 0], transform: ['translateY(0px)', 'translateY(-24px)'] },
            { duration: DRAWER_FADE_DURATION, ease: EASE }
          );
        }
      } else if (M && M.animate) {
        drawerAnim = M.animate(drawer, { opacity: [1, 0] }, { duration: DRAWER_FADE_DURATION, ease: EASE });
      }

      fadeScrim(scrim.style.top ? parseInt(scrim.style.top, 10) : 0, 0, DRAWER_FADE_DURATION);

      // Restore the bar: backdrop slides back down, icons revert, and its own
      // logo+links accordion back in the same way they do on scroll-appear.
      exitAnims.forEach((a) => a && a.cancel && a.cancel());
      exitAnims = [];
      if (backdropAnim && backdropAnim.cancel) backdropAnim.cancel();
      compactBarBackdrop.style.transform = 'translateY(0%)';
      if (M && M.animate) {
        backdropAnim = M.animate(
          compactBarBackdrop,
          { transform: ['translateY(-100%)', 'translateY(0%)'] },
          { duration: BAR_SWAP_DURATION, ease: EASE }
        );
      }
      compactBarIconImgs.forEach((img) => img.classList.remove('fnav-hero-icon'));
      accordionIn([compactBarLogoMark, compactBarWordmark, ...links]);
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        openDrawer(trigger);
      });
      // About and Teams have no landing page: a click only keeps the menu
      // open, rather than following "#" back to the top of the page
      trigger.addEventListener('click', (e) => {
        if (trigger.getAttribute('href') === '#') e.preventDefault();
      });
    });

    // Closes a short beat after the cursor leaves the drawer, rather than the
    // instant it does — moving from the drawer back up to a trigger (or just a
    // slightly indirect path out) crosses outside its box for a moment, and a
    // bare mouseleave would dismiss the menu mid-move instead of registering as
    // "still browsing." The delay is canceled if the cursor re-enters the
    // drawer or a trigger (which reopens it) before it fires.
    drawer.addEventListener('mouseleave', () => {
      if (!drawerOpen) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => closeDrawer(), 200);
    });
    drawer.addEventListener('mouseenter', () => clearTimeout(closeTimer));

    // Click-outside-close stays as a fallback (e.g. a stray mouseleave that
    // doesn't fire, or a trackpad/touch hybrid with no real hover) alongside
    // the mouseleave behavior above, not in place of it.
    document.addEventListener('click', (e) => {
      if (drawerOpen && !drawer.contains(e.target)) {
        closeDrawer();
      }
    });
  })();

  // Second-level category expansion (Scrubs, Underscrubs, etc.) inside the Women
  // drawer. Clicking one grays out its siblings, flips its "+" to "−", and shows
  // its sub-links + swaps in real imagery where the design has it (Scrubs only —
  // the other categories only have placeholder art in the source file).
  (() => {
    // Keyed by gender first since Men's assortment isn't just a filtered view of
    // Women's — Scrubs drops Jumpsuits/Leggings/Maternity and gets its own
    // photography, and the whole Jewelry category doesn't exist (node 943:14558
    // vs the Women's drawer: five categories, no Jewelry chip/link at all).
    // Shop by Fabric (node 515:107333) — shared across genders (Figma shows no
    // Women's/Men's variant), and unlike every other category it has no
    // sublevel link list at all: expanding it only swaps the 3 image cards to
    // named-fabric callouts (see the `captions` field, read by expandSublevel's
    // setImageCaptions instead of the usual date/title pair).
    const FABRIC_DATA = {
      links: [],
      images: [asset('nav-fabric-0.jpg'), asset('nav-fabric-1.jpg'), asset('nav-fabric-2.jpg')],
      captions: [
        { name: 'FIONx™', desc: 'Soft and breathable' },
        { name: 'formx™', desc: 'Stretch and comfort' },
        { name: 'FIBREx™', desc: 'Durable and lightweight' },
      ],
    };

    const SUBNAV_DATA = {
      women: {
        scrubs: {
          links: ['Scrub Sets', 'All Scrubs', 'Scrub Tops', 'Scrub Pants', 'ScrubJumpsuits™', 'ScrubLeggings™', 'Maternity'],
          images: [asset('nav-scrubs-0.jpg'), asset('nav-scrubs-1.jpg'), asset('nav-scrubs-2.jpg')],
        },
        underscrubs: {
          links: ['Underscrubs', 'Tees', 'Graphic Tees'],
        },
        outerwear: {
          links: ['Jackets and Vests'],
        },
        // parents of their own since 2026-10-15 (were under Underscrubs / Outerwear)
        loungewear: {
          links: ['All Loungewear'],
        },
        'lab-coats': {
          links: ['All Lab Coats'],
        },
        shoes: {
          links: ['FIGS | New Balance', 'Socks'],
        },
        accessories: {
          links: ['All Accessories', 'Pins', 'Bags'],
        },
        jewelry: {
          links: ['All Jewelry', 'Necklaces', 'Rings', 'Earrings', 'Bracelets', 'Pins', 'By Profession'],
        },
        fabric: FABRIC_DATA,
      },
      men: {
        scrubs: {
          links: ['Scrub Sets', 'All Scrubs', 'Scrub Tops', 'Scrub Pants'],
          images: [asset('nav-men-scrubs-0.jpg'), asset('nav-men-scrubs-1.jpg'), asset('nav-men-scrubs-2.jpg')],
        },
        underscrubs: {
          links: ['Underscrubs', 'Tees', 'Graphic Tees'],
        },
        outerwear: {
          links: ['Jackets and Vests'],
        },
        // parents of their own since 2026-10-15 (were under Underscrubs / Outerwear)
        loungewear: {
          links: ['All Loungewear'],
        },
        'lab-coats': {
          links: ['All Lab Coats'],
        },
        shoes: {
          links: ['FIGS | New Balance', 'Socks'],
        },
        accessories: {
          links: ['All Accessories', 'Pins', 'Bags'],
        },
        fabric: FABRIC_DATA,
      },
    };

    // Generic editorial imagery shown whenever a category has no curated
    // `images` of its own (every non-Scrubs category, plus any non-curated
    // color swatch) — one set per gender so Men's drawer never shows Women's
    // campaign photography as its fallback.
    const DEFAULT_IMAGES = {
      women: [asset('nav-default-0.jpg'), asset('nav-default-1.jpg'), asset('nav-default-2.jpg')],
      men: [asset('nav-men-default-0.jpg'), asset('nav-men-default-1.jpg'), asset('nav-men-default-2.jpg')],
    };
    function fallbackImages() {
      return DEFAULT_IMAGES[currentGender] || DEFAULT_IMAGES.women;
    }

    // The 8x7 grid (8 columns, 7 rows; flat, row-major) from treed's "Shop by
    // Color" color asset (2026-10-18) — each hex sampled from the centre of its
    // cell, with COLOR_NAMES in the same order. Names are verbatim from that
    // asset, F*ing Pink included (it's the real FIGS color name).
    const COLOR_GRID = [
      '#9a5538', '#66784f', '#2d6049', '#315e4f', '#138b68', '#0b7b78', '#285c5b', '#3b3d3f',
      '#8b7a73', '#c1976e', '#a6a227', '#77a85b', '#38a969', '#168f91', '#1aa7be', '#4d5053',
      '#57372c', '#e87555', '#eaa496', '#f7bea3', '#eab52b', '#f6d86c', '#8bafc7', '#5c6670',
      '#d92e38', '#f18fa9', '#efb7c2', '#f6f6f2', '#ece3d9', '#a8bdb5', '#72899a', '#28588e',
      '#c91f34', '#e84e7b', '#ffaac0', '#c9b8d7', '#d8d9db', '#6fcbbc', '#95acbf', '#285798',
      '#93435a', '#ff6f9e', '#d85c85', '#b89ead', '#8f7cab', '#aaadb0', '#6e91b0', '#294a80',
      '#74253e', '#712b45', '#7f275d', '#654071', '#7969aa', '#4e5877', '#202b4d', '#111214',
    ];
    const COLOR_NAMES = [
      'Spiced Cider', 'Moss', 'Forest Green', 'Spruce', 'Emerald', 'Deep Reef', 'Dark Harbor', 'Charcoal',
      'Smokey Taupe', 'Sandstone', 'Martini Olive', 'Jade', 'Surgical Green', 'Lagoon', 'Caribbean Blue', 'Graphite',
      'Espresso', 'Papaya', 'Pink Sand', 'Creamsicle', 'Dandelion', 'Butter Yellow', 'Ceil Blue', 'Slate',
      'Pop Red', 'Chalk Pink', 'Pulmonary Pink', 'Optic White', 'Off White', 'Sagewater', 'Heather Denim', 'Sapphire',
      'Winning Red', 'Rapid Response Pink', 'Fight Club Pink', 'Lavender Dew', 'Silver Frost', 'Sea Mist', 'Dusty Blue', 'Royal Blue',
      'Berry Spice', 'F*ing Pink', 'Hibiscus', 'Mauve', 'Purple Haze', 'Cement', 'Blue Horizon', 'Deep Royal Blue',
      'Deep Cranberry', 'Burgundy', 'Ultraberry', 'Purple Pulse', 'Wild Iris', 'Heather Indigo', 'Navy', 'Black',
    ];
    const DEFAULT_COLOR_INDEX = 39; // Royal Blue (row 5, col 8) — auto-selected on open (node 943:19624)
    const CHEVRON_RIGHT_SVG = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.93945 5L2.94043 10L2.06055 9.12012L6.18066 5L2.06055 0.879883L2.94043 0L7.93945 5Z" fill="currentColor"/></svg>';
    // Three real product shots per color, pulled from wearfigs.com (each PDP's
    // own gallery in that exact color) and saved as nav-color-<slug>-0/1/2.jpg —
    // Royal Blue keeps the original Figma photography. Sandstone, Papaya, Pink
    // Sand, Dusty Blue and Cement aren't sold on wearfigs.com any more, so they
    // get no entry and preview the generic default media instead (same as any
    // other uncurated category). Lavender Dew only survives as jewelry (the
    // Stethoscope Enamel Ring and Chill Pill earrings), so its cards show those.
    // URLs only resolve here; nothing is fetched until a swatch is shown.
    const COLORS_WITHOUT_PHOTOGRAPHY = new Set(['Sandstone', 'Papaya', 'Pink Sand', 'Dusty Blue', 'Cement']);
    const colorSlug = (name) => name.toLowerCase().replace(/\*/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const COLOR_IMAGES = {};
    COLOR_NAMES.forEach((name, i) => {
      if (COLORS_WITHOUT_PHOTOGRAPHY.has(name)) return;
      COLOR_IMAGES[i] = [0, 1, 2].map((n) => asset(`nav-color-${colorSlug(name)}-${n}.jpg`));
    });
    // Optic White / Off White would vanish into the white drawer as the 9px
    // label chip, so near-white swatches get the nav's hairline (see
    // .fnav-nav-drawer-color-label-swatch.is-light).
    function isLightColor(hex) {
      const [r, g, b] = [1, 3, 5].map((o) => parseInt(hex.slice(o, o + 2), 16) / 255);
      return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.88;
    }

    const drawer = document.getElementById('fnav-nav-drawer');
    const primaryGroup = document.getElementById('fnav-nav-drawer-primary');
    const imagesColumn = document.getElementById('fnav-nav-drawer-images');
    const sublevel = document.getElementById('fnav-nav-drawer-sublevel');
    const imageCards = document.querySelectorAll('#fnav-nav-drawer-image-cards .fnav-nav-drawer-image-card');
    // The date/title caption baked into each card's static HTML — captured
    // once up front so setImageCaptions can restore it for every category
    // that isn't Shop by Fabric, rather than hardcoding it a second time here.
    const DEFAULT_CAPTIONS = Array.from(imageCards).map((card) => ({
      date: card.querySelector('.fnav-nav-drawer-image-date').textContent,
      title: card.querySelector('.fnav-nav-drawer-image-title').textContent,
    }));

    // Shop by Fabric swaps the usual date+title caption for a name+description
    // pair (see the .is-fabric CSS, which restacks the same two spans
    // vertically) — pass `captions` to use it, or omit/null to restore the
    // default date/title for every other category.
    // The three cards link where their photos lead: the colour's page in Shop
    // by Color, the fabrics PLP in Shop by Fabric, otherwise nowhere yet.
    function setImageCaptions(captions, href = captions ? LAB.classicFit : null) {
      imageCards.forEach((card, i) => {
        card.href = href || '#';
        const caption = card.querySelector('.fnav-nav-drawer-image-caption');
        const dateEl = card.querySelector('.fnav-nav-drawer-image-date');
        const titleEl = card.querySelector('.fnav-nav-drawer-image-title');
        const custom = captions && captions[i];
        if (custom) {
          caption.classList.add('is-fabric');
          dateEl.textContent = custom.desc;
          titleEl.textContent = custom.name;
        } else {
          caption.classList.remove('is-fabric');
          const def = DEFAULT_CAPTIONS[i] || DEFAULT_CAPTIONS[0];
          dateEl.textContent = def.date;
          titleEl.textContent = def.title;
        }
      });
    }
    // Scoped to the whole links column, not just primaryGroup — "Shop by Color"
    // is expandable too, but lives in the group above (New Arrivals/Best Sellers).
    const expandables = drawer.querySelectorAll('.fnav-nav-drawer-links .fnav-nav-drawer-expandable');
    // Every link in the drawer's left column (all four groups) — grayed out as a
    // set whenever a category is active, leaving only the active link at full color.
    const allDrawerLinks = drawer.querySelectorAll(
      '.fnav-nav-drawer-links .fnav-nav-drawer-heading, .fnav-nav-drawer-links .fnav-nav-drawer-sub, .fnav-teams__main .fnav-nav-drawer-heading'
    );

    const EASE = [0.4, 0, 0.2, 1];

    // Same "accordion in" stagger used elsewhere in the drawer (see the compact
    // bar / header logo — this IIFE has its own copy since it's a separate scope).
    function accordionIn(elements, baseDelay = 0.1, stagger = 0.06, duration = 0.4) {
      const M = Motion;
      if (!M || !M.animate || !elements.length) {
        elements.forEach((el) => {
          el.style.opacity = '';
          el.style.transform = '';
        });
        return [];
      }
      const staggerFn = M.stagger ? M.stagger(stagger) : (i) => i * stagger;
      return elements.map((el, i) => {
        el.style.transform = 'translateX(-24px)';
        el.style.opacity = '0';
        const delay = typeof staggerFn === 'function' ? staggerFn(i, { total: elements.length }) : 0;
        const anim = M.animate(
          el,
          { transform: ['translateX(-24px)', 'translateX(0px)'], opacity: [0, 1] },
          { duration, delay: baseDelay + delay, ease: EASE }
        );
        setTimeout(() => {
          el.style.transform = 'translateX(0px)';
          el.style.opacity = '1';
        }, (baseDelay + delay + duration) * 1000);
        return anim;
      });
    }

    let sublevelChildAnims = [];

    // Same hover-dim pattern as the primary column (updateLinkGraying below), but
    // scoped to just the sublevel's own heading/links — an independent group.
    let sublevelHoveredLink = null;
    let sublevelGrayClearTimer = null;
    function updateSublevelGraying() {
      sublevel.querySelectorAll('.fnav-nav-drawer-heading, .fnav-nav-drawer-sub').forEach((link) => {
        const isCurrent = link === sublevelHoveredLink;
        link.classList.toggle('is-grayed', Boolean(sublevelHoveredLink) && !isCurrent);
        link.classList.toggle('is-current', isCurrent);
      });
    }

    // Fills in the sublevel's heading/links and swaps the image cards' photos,
    // then runs the SAME left-to-right accordion the compact bar/header links use
    // across all of it (heading, links, and media together) — no height push.
    function expandSublevel(data) {
      sublevelChildAnims.forEach((a) => a && a.cancel && a.cancel());
      imagesColumn.scrollTop = 0;

      sublevel.innerHTML = '';
      const children = [];
      if (data.heading) {
        const h = document.createElement('span');
        h.className = 'fnav-nav-drawer-heading';
        h.textContent = data.heading;
        sublevel.appendChild(h);
        children.push(h);
      }
      data.links.forEach((label) => {
        const a = document.createElement('a');
        a.href = SUBNAV_HREFS[currentGender]?.[label] || '#';
        a.className = 'fnav-nav-drawer-sub';
        a.textContent = label;
        sublevel.appendChild(a);
        children.push(a);
      });

      sublevelHoveredLink = null;
      children.forEach((link) => {
        link.addEventListener('mouseenter', () => {
          clearTimeout(sublevelGrayClearTimer);
          sublevelHoveredLink = link;
          updateSublevelGraying();
        });
        // Same adjacent-link-gap debounce as allDrawerLinks' mouseleave below.
        link.addEventListener('mouseleave', () => {
          clearTimeout(sublevelGrayClearTimer);
          sublevelGrayClearTimer = setTimeout(() => {
            if (sublevelHoveredLink === link) {
              sublevelHoveredLink = null;
              updateSublevelGraying();
            }
          }, 100);
        });
      });

      const sublevelImages = data.images || fallbackImages();
      imageCards.forEach((card, i) => {
        card.style.backgroundImage = sublevelImages[i] ? `url(${sublevelImages[i]})` : '';
      });
      setImageCaptions(data.captions);

      sublevelChildAnims = accordionIn([...children, ...imageCards]);
    }

    function collapseSublevel() {
      sublevelChildAnims.forEach((a) => a && a.cancel && a.cancel());
      sublevelChildAnims = [];
      sublevelHoveredLink = null;
      sublevel.innerHTML = '';
    }

    let activeCategory = null;
    let hoveredLink = null;

    // Graying is driven by whichever takes precedence: a hovered link (temporary,
    // wins over everything while the pointer is over it) or the active/expanded
    // category (persists after the pointer leaves). Neither hovered -> no graying.
    function updateLinkGraying() {
      const somethingActive = Boolean(hoveredLink) || Boolean(activeCategory);
      allDrawerLinks.forEach((link) => {
        const isCurrent = hoveredLink
          ? link === hoveredLink
          : Boolean(activeCategory) && link.dataset.category === activeCategory;
        link.classList.toggle('is-grayed', somethingActive && !isCurrent);
        link.classList.toggle('is-current', somethingActive && isCurrent);
      });
    }

    // Adjacent links have a real 12px gap between them (.nav-drawer-group's
    // own gap) — crossing it takes actual, if brief, time with the pointer
    // over neither link, so a same-tick microtask guard isn't enough (that
    // only covers the case where the browser happens to fire leave/enter back
    // to back with no gap in between). A short debounce on the "clear" side
    // tolerates that gap: moving the pointer from one link to the next
    // re-claims hoveredLink well within the delay, so the stale clear below
    // finds it's no longer "this" link and no-ops instead of graying
    // everything off and back on — which is what was showing up as Jewelry's
    // shimmer flashing to full brightness on every hover change elsewhere in
    // the list. Entering a link is still instant (no delay on mouseenter).
    let grayClearTimer = null;
    allDrawerLinks.forEach((link) => {
      link.addEventListener('mouseenter', () => {
        clearTimeout(grayClearTimer);
        hoveredLink = link;
        updateLinkGraying();
      });
      link.addEventListener('mouseleave', () => {
        clearTimeout(grayClearTimer);
        grayClearTimer = setTimeout(() => {
          if (hoveredLink === link) {
            hoveredLink = null;
            updateLinkGraying();
          }
        }, 100);
      });
    });

    // The committed selection (set by a click) — survives mouseleave and is
    // what the grid reverts to once the pointer wanders off and off a
    // not-yet-clicked preview. Starts on Royal Blue (node 943:19624).
    let selectedColorIndex = DEFAULT_COLOR_INDEX;

    // Updates the swatch highlight + label + media for `index` without
    // touching `selectedColorIndex` — used for both the committed selection
    // and a transient hover preview, so hovering around and leaving without
    // clicking leaves no trace on the committed state.
    function applyColorVisual(index) {
      sublevel.querySelectorAll('.fnav-nav-drawer-color-swatch').forEach((sw, i) => {
        sw.classList.toggle('is-selected', i === index);
      });

      // Selection is always non-null now (a color is always selected by
      // default/persistently), and every swatch has a name — the placeholder
      // only guards against a grid/name length mismatch, not a prompt to
      // select something, since something already is.
      const label = document.getElementById('fnav-nav-drawer-color-label');
      label.classList.toggle('is-color-active', index != null);
      label.innerHTML = '';
      if (index != null) {
        const mini = document.createElement('span');
        mini.className = 'fnav-nav-drawer-color-label-swatch';
        mini.style.background = COLOR_GRID[index];
        mini.classList.toggle('is-light', isLightColor(COLOR_GRID[index]));
        const nameEl = document.createElement('span');
        nameEl.className = 'fnav-nav-drawer-color-label-name';
        nameEl.textContent = COLOR_NAMES[index] || 'Color Name';
        const shop = document.createElement('a');
        shop.href = labColorHref(COLOR_NAMES[index]) || '#';
        shop.className = 'fnav-nav-drawer-color-label-shop';
        shop.innerHTML = `Shop${CHEVRON_RIGHT_SVG}`;
        label.append(mini, nameEl, shop);
      } else {
        label.textContent = 'Select a color';
      }

      const images = index != null ? COLOR_IMAGES[index] || fallbackImages() : null;
      imageCards.forEach((card, i) => {
        card.style.backgroundImage = images && images[i] ? `url(${images[i]})` : '';
      });
      // the colour's photos go where its Shop link does
      setImageCaptions(null, index != null ? labColorHref(COLOR_NAMES[index]) : null);
    }

    // Click commits the selection — hovering a swatch only previews it (see
    // applyColorVisual's mouseenter wiring below) until it's actually clicked.
    function selectColor(index) {
      selectedColorIndex = index;
      applyColorVisual(index);
    }

    function renderColorGrid() {
      sublevelChildAnims.forEach((a) => a && a.cancel && a.cancel());
      sublevelHoveredLink = null;

      sublevel.innerHTML = '';
      const grid = document.createElement('div');
      grid.className = 'fnav-nav-drawer-color-grid';
      COLOR_GRID.forEach((hex, i) => {
        const sw = document.createElement('button');
        sw.type = 'button';
        sw.className = 'fnav-nav-drawer-color-swatch';
        sw.style.background = hex;
        sw.setAttribute('aria-label', COLOR_NAMES[i] || 'Color swatch');
        sw.addEventListener('mouseenter', () => applyColorVisual(i));
        sw.addEventListener('click', () => selectColor(i));
        grid.appendChild(sw);
      });
      // Leaving the grid without clicking reverts the preview back to
      // whatever was last actually selected (the default, if nothing has
      // been clicked yet this visit).
      grid.addEventListener('mouseleave', () => applyColorVisual(selectedColorIndex));
      sublevel.appendChild(grid);

      const label = document.createElement('div');
      label.className = 'fnav-nav-drawer-color-label';
      label.id = 'fnav-nav-drawer-color-label';
      sublevel.appendChild(label);

      applyColorVisual(selectedColorIndex);
      sublevelChildAnims = accordionIn([grid, label, ...imageCards]);
    }

    function render(category) {
      if (category === 'color') {
        renderColorGrid();
        return;
      }
      const data = SUBNAV_DATA[currentGender][category];
      if (!data) return;
      expandSublevel(data);
    }

    // The shimmer's `animation-iteration-count: 3` means it plays itself out
    // (3 passes, ~21s) and then just sits still — so if a shopper clicks
    // Jewelry after that initial run has already finished, selecting it
    // wouldn't visibly shimmer at all. Restarting the animation (via the
    // standard remove/reflow/re-add trick, since you can't just re-trigger a
    // class-driven animation by toggling the class that was never removed)
    // replays it every time the category becomes active, no-op for every
    // other category since they have no .shimmer-text to find.
    function restartShimmer(container) {
      const shimmer = container.querySelector('.fnav-shimmer-text');
      if (!shimmer) return;
      shimmer.style.animation = 'none';
      void shimmer.offsetWidth;
      shimmer.style.animation = '';
    }

    function setActive(category) {
      activeCategory = category;
      expandables.forEach((link) => {
        const isNowActive = link.dataset.category === category;
        link.classList.toggle('is-expanded', isNowActive);
        if (isNowActive) restartShimmer(link);
      });
      updateLinkGraying();
      render(category);
    }

    function clearActive() {
      activeCategory = null;
      expandables.forEach((link) => {
        link.classList.remove('is-expanded');
      });
      updateLinkGraying();
      collapseSublevel();
      const images = fallbackImages();
      imageCards.forEach((card, i) => {
        card.style.backgroundImage = images[i] ? `url(${images[i]})` : '';
        card.style.opacity = '';
        card.style.transform = '';
      });
      setImageCaptions(null);
    }

    expandables.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const category = link.dataset.category;
        if (category === activeCategory) {
          clearActive();
        } else {
          setActive(category);
        }
      });
    });

    drawer.addEventListener('fnav-nav-drawer:close', clearActive);

    // Mobile nav drawer — a separate full-screen menu (see styles.css for why),
    // triggered by the hamburger in either masthead. Reuses SUBNAV_DATA for the
    // chip row's sublists/carousel images rather than duplicating that data.
    const mobileScrim = document.getElementById('fnav-mobile-nav-scrim');
    const mobileDrawer = document.getElementById('fnav-mobile-nav-drawer');
    const mobileClose = document.getElementById('fnav-mobile-nav-close');
    const mobileHamburgers = document.querySelectorAll('.fnav-mobile-hamburger');
    // Excludes the About sub-tabs (#mobile-nav-chips-about) — they share the
    // .mobile-chip class purely for visual styling, but are wired up
    // separately below (renderMobileAboutSection) since they drive
    // ABOUT_SECTIONS, not SUBNAV_DATA[currentGender].
    const mobileChips = document.querySelectorAll('.fnav-mobile-chip:not([data-about])');
    const mobileChipsWrap = document.querySelector('.fnav-mobile-nav-chips-wrap');
    const mobileSublevel = document.getElementById('fnav-mobile-nav-sublevel');
    const mobileCarousel = document.getElementById('fnav-mobile-nav-carousel');
    const mobileShopByColor = document.getElementById('fnav-mobile-shop-by-color');
    const mobileShopByFabric = document.getElementById('fnav-mobile-shop-by-fabric');
    const mobileChipsAbout = document.querySelectorAll('.fnav-mobile-nav-chips-about .fnav-mobile-chip');

    let mobileDrawerOpen = false;
    let mobileDrawerAnim = null;
    let mobileScrimAnim = null;
    let mobileActiveCategory = 'scrubs';
    let mobileColorMode = false;
    let mobileFabricMode = false;
    let mobileSelectedColorIndex = DEFAULT_COLOR_INDEX;

    // About's mobile sub-tabs (node 518:107354) — same two link groups as the
    // desktop static menu, just one at a time instead of both stacked. Both
    // share the single real photo/caption below (Figma gives no alternate
    // imagery for Our Impact).
    const ABOUT_SECTIONS = {
      about: { links: ['Our Story', 'Fabric Technology', 'Personalize Your Scrubs', 'Events & Community'] },
      impact: { links: ['FIGS Impact Hub', 'Advocacy Hub', 'Scrubs That Don’t Suck'] },
    };
    const ABOUT_IMAGE = asset('nav-about-0.jpg');
    const ABOUT_CAPTION = 'The Awesome Humans Foundation';
    let mobileAboutSection = 'about';

    function fillMobileCarousel(images, href = null) {
      mobileCarousel.innerHTML = '';
      const list = images && images.length ? images : [null, null, null];
      list.forEach((src) => {
        const card = document.createElement('a');
        card.href = href || '#';
        card.className = 'fnav-mobile-nav-carousel-card';
        if (src) card.style.backgroundImage = `url(${src})`;
        mobileCarousel.appendChild(card);
      });
    }

    function renderMobileCategory(category) {
      mobileChips.forEach((chip) => {
        const isNowActive = chip.dataset.category === category;
        chip.classList.toggle('is-active', isNowActive);
        if (isNowActive) restartShimmer(chip);
      });

      const data = SUBNAV_DATA[currentGender][category];
      mobileSublevel.innerHTML = '';
      if (!data) {
        fillMobileCarousel(fallbackImages());
        return;
      }

      if (data.heading) {
        const h = document.createElement('span');
        h.className = 'fnav-nav-drawer-heading';
        h.textContent = data.heading;
        mobileSublevel.appendChild(h);
      }
      data.links.forEach((label) => {
        const a = document.createElement('a');
        a.href = SUBNAV_HREFS[currentGender]?.[label] || '#';
        a.className = 'fnav-nav-drawer-sub';
        a.textContent = label;
        mobileSublevel.appendChild(a);
      });

      fillMobileCarousel(data.images || fallbackImages());
    }

    // Selects a swatch in the mobile color grid — same persistent-selection
    // model as the desktop grid (see selectColor above), just rendered at the
    // mobile scale (node 943:19742: 44.8px swatches, 53.7px when selected).
    function selectMobileColor(index) {
      mobileSelectedColorIndex = index;
      mobileSublevel.querySelectorAll('.fnav-nav-drawer-color-swatch').forEach((sw, i) => {
        sw.classList.toggle('is-selected', i === index);
      });
      const label = mobileSublevel.querySelector('.fnav-nav-drawer-color-label');
      if (!label) return;
      label.classList.add('is-color-active');
      label.innerHTML = '';
      const mini = document.createElement('span');
      mini.className = 'fnav-nav-drawer-color-label-swatch';
      mini.style.background = COLOR_GRID[index];
      mini.classList.toggle('is-light', isLightColor(COLOR_GRID[index]));
      const nameEl = document.createElement('span');
      nameEl.className = 'fnav-nav-drawer-color-label-name';
      nameEl.textContent = COLOR_NAMES[index] || 'Color Name';
      const shop = document.createElement('a');
      shop.href = labColorHref(COLOR_NAMES[index]) || '#';
      shop.className = 'fnav-nav-drawer-color-label-shop';
      shop.innerHTML = `Shop${CHEVRON_RIGHT_SVG}`;
      label.append(mini, nameEl, shop);
      fillMobileCarousel(COLOR_IMAGES[index] || fallbackImages(), labColorHref(COLOR_NAMES[index]));
    }

    function renderMobileColorGrid() {
      mobileSublevel.innerHTML = '';

      // The dedicated page has no persistent left-column label to lean on (see
      // enterMobileColorMode), so it repeats "SHOP BY COLOR" as its own heading.
      const heading = document.createElement('span');
      heading.className = 'fnav-nav-drawer-heading fnav-mobile-nav-color-heading';
      heading.textContent = 'Shop by Color';
      mobileSublevel.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'fnav-nav-drawer-color-grid';
      COLOR_GRID.forEach((hex, i) => {
        const sw = document.createElement('button');
        sw.type = 'button';
        sw.className = 'fnav-nav-drawer-color-swatch';
        sw.style.background = hex;
        sw.setAttribute('aria-label', COLOR_NAMES[i] || 'Color swatch');
        sw.addEventListener('click', () => selectMobileColor(i));
        grid.appendChild(sw);
      });
      mobileSublevel.appendChild(grid);

      const label = document.createElement('div');
      label.className = 'fnav-nav-drawer-color-label';
      mobileSublevel.appendChild(label);

      selectMobileColor(mobileSelectedColorIndex);
    }

    // Shop by Fabric's mobile page (node 1111:27345) — a single vertical stack
    // of 3 named-fabric cards rendered into .mobile-nav-sublevel, the same slot
    // the color grid uses. No swatches/links here, just the stack itself.
    function renderMobileFabricPage() {
      mobileSublevel.innerHTML = '';

      const heading = document.createElement('span');
      heading.className = 'fnav-nav-drawer-heading fnav-mobile-nav-color-heading';
      heading.textContent = 'Shop by Fabric';
      mobileSublevel.appendChild(heading);

      const stack = document.createElement('div');
      stack.className = 'fnav-mobile-fabric-stack';
      FABRIC_DATA.captions.forEach((caption, i) => {
        const card = document.createElement('a');
        card.href = LAB.classicFit;
        card.className = 'fnav-mobile-fabric-card';
        card.style.backgroundImage = `url(${FABRIC_DATA.images[i]})`;

        const captionEl = document.createElement('span');
        captionEl.className = 'fnav-nav-drawer-image-caption is-fabric';
        const descEl = document.createElement('span');
        descEl.className = 'fnav-nav-drawer-image-date';
        descEl.textContent = caption.desc;
        const nameEl = document.createElement('span');
        nameEl.className = 'fnav-nav-drawer-image-title';
        nameEl.textContent = caption.name;
        captionEl.append(descEl, nameEl);
        card.appendChild(captionEl);

        stack.appendChild(card);
      });
      mobileSublevel.appendChild(stack);
    }

    function enterMobileFabricMode() {
      mobileFabricMode = true;
      mobileShopByFabric.classList.add('is-current');
      mobileChipsWrap.style.display = 'none';
      mobileDrawer.classList.add('is-fabric-page');
      mobileClose.classList.add('is-back');
      mobileClose.setAttribute('aria-label', 'Back to menu');
      // The trigger link lives in the footer, so reaching it means scrolling
      // .mobile-nav-drawer (the scrollable panel) most of the way down —
      // without this the dedicated page would open already scrolled past
      // its own heading.
      mobileDrawer.scrollTop = 0;
      // Shop by Fabric doesn't use the shared carousel — empty it so the
      // :empty rule hides it instead of showing whatever category was last active.
      mobileCarousel.innerHTML = '';
      renderMobileFabricPage();
    }

    function exitMobileFabricMode() {
      mobileFabricMode = false;
      mobileShopByFabric.classList.remove('is-current');
      mobileChipsWrap.style.display = '';
      mobileDrawer.classList.remove('is-fabric-page');
      mobileClose.classList.remove('is-back');
      mobileClose.setAttribute('aria-label', 'Close menu');
      renderMobileCategory(mobileActiveCategory);
    }

    mobileShopByFabric.addEventListener('click', (e) => {
      e.preventDefault();
      if (mobileFabricMode) {
        exitMobileFabricMode();
      } else {
        enterMobileFabricMode();
      }
    });

    // Renders one of the three About sub-tabs into the regular
    // sublevel/carousel slots — unlike Color/Fabric, About reuses the normal
    // chip-driven layout (just with #mobile-nav-chips-about's buttons instead
    // of SUBNAV_DATA's categories), so the close button stays a plain X rather
    // than becoming a back-chevron (node 1138:29553 — no back affordance).
    function renderMobileAboutSection(key) {
      mobileAboutSection = key;
      mobileChipsAbout.forEach((chip) => {
        chip.classList.toggle('is-active', chip.dataset.about === key);
      });

      mobileSublevel.innerHTML = '';
      ABOUT_SECTIONS[key].links.forEach((label) => {
        const a = document.createElement('a');
        a.href = ABOUT_HREFS[label] || '#';
        a.className = 'fnav-nav-drawer-sub';
        a.textContent = label;
        mobileSublevel.appendChild(a);
      });

      mobileCarousel.classList.add('is-about-carousel');
      mobileCarousel.innerHTML = '';
      const card = document.createElement('a');
      card.href = '#';
      card.className = 'fnav-mobile-nav-carousel-card fnav-mobile-nav-carousel-card-about';
      card.style.backgroundImage = `url(${ABOUT_IMAGE})`;
      const caption = document.createElement('span');
      caption.className = 'fnav-nav-drawer-image-caption is-about';
      const title = document.createElement('span');
      title.className = 'fnav-nav-drawer-image-title';
      title.textContent = ABOUT_CAPTION;
      caption.appendChild(title);
      card.appendChild(caption);
      mobileCarousel.appendChild(card);
    }

    function enterMobileAboutMode() {
      mobileDrawer.classList.add('is-about-page');
      mobileDrawer.scrollTop = 0;
      renderMobileAboutSection('about');
    }

    function exitMobileAboutMode() {
      mobileDrawer.classList.remove('is-about-page');
      mobileCarousel.classList.remove('is-about-carousel');
      mobileActiveCategory = 'scrubs';
      renderMobileCategory(mobileActiveCategory);
    }

    mobileChipsAbout.forEach((chip) => {
      chip.addEventListener('click', () => {
        renderMobileAboutSection(chip.dataset.about);
      });
    });

    function enterMobileColorMode() {
      mobileColorMode = true;
      mobileShopByColor.classList.add('is-current');
      mobileChipsWrap.style.display = 'none';
      mobileDrawer.classList.add('is-color-page');
      mobileClose.classList.add('is-back');
      mobileClose.setAttribute('aria-label', 'Back to menu');
      // Same reasoning as enterMobileFabricMode — the trigger is a highlight
      // link, not necessarily at the top of the scrollable drawer panel.
      mobileDrawer.scrollTop = 0;
      mobileCarousel.scrollLeft = 0;
      renderMobileColorGrid();
    }

    function exitMobileColorMode() {
      mobileColorMode = false;
      mobileShopByColor.classList.remove('is-current');
      mobileChipsWrap.style.display = '';
      mobileDrawer.classList.remove('is-color-page');
      mobileClose.classList.remove('is-back');
      mobileClose.setAttribute('aria-label', 'Close menu');
      mobileCarousel.scrollLeft = 0;
      renderMobileCategory(mobileActiveCategory);
    }

    mobileShopByColor.addEventListener('click', (e) => {
      e.preventDefault();
      if (mobileColorMode) {
        exitMobileColorMode();
      } else {
        enterMobileColorMode();
      }
    });

    mobileChips.forEach((chip) => {
      chip.addEventListener('click', () => {
        mobileActiveCategory = chip.dataset.category;
        mobileCarousel.scrollLeft = 0;
        if (mobileColorMode) {
          mobileColorMode = false;
          mobileShopByColor.classList.remove('is-current');
          mobileChipsWrap.style.display = '';
        }
        if (mobileFabricMode) {
          mobileFabricMode = false;
          mobileShopByFabric.classList.remove('is-current');
          mobileChipsWrap.style.display = '';
          mobileDrawer.classList.remove('is-fabric-page');
          mobileClose.classList.remove('is-back');
          mobileClose.setAttribute('aria-label', 'Close menu');
        }
        renderMobileCategory(mobileActiveCategory);
      });
    });

    renderMobileCategory(mobileActiveCategory);

    // Tapping a tab inside the already-open mobile drawer (as opposed to
    // hovering in the desktop header) swaps section without closing the sheet.
    document.querySelectorAll('.fnav-mobile-nav-tabs [data-fnav-drawer]').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        setSection(tab.dataset.fnavDrawer);
      });
    });

    // A gender switch only actually changes two things — the Jewelry link and
    // the imagery — and both swap instantly, same as every other category
    // change in this drawer. Fading the imagery was tried three ways (a
    // full-panel opacity fade, a scoped opacity fade, a brightness-filter dip)
    // and each produced its own visible artifact — a full flash, a flash to
    // white (opacity composites against the drawer's white background, which
    // brightens dark product photos instead of dimming them), then a hard dip
    // to black (confirmed by sampling screen-recording frames: brightness
    // undershot the darker of the two end states every time). An instant swap
    // has no animation to get wrong.
    //
    // Fires from setGender (desktop hover swap or a mobile tab tap) — resets
    // both drawers back to their default, nothing-expanded state rather than
    // carrying over a category (e.g. Jewelry) that doesn't exist for the other
    // gender.
    drawer.addEventListener('fnav-nav-drawer:gender-change', () => {
      clearActive();
      mobileActiveCategory = 'scrubs';
      if (mobileColorMode) {
        exitMobileColorMode();
      } else if (mobileFabricMode) {
        exitMobileFabricMode();
      } else {
        renderMobileCategory(mobileActiveCategory);
      }
    });

    // Fires from setSection — About / Teams entry and exit. Exiting falls
    // through to setGender right after, which fires nav-drawer:gender-change
    // too; the repeated clearActive()/renderMobileCategory there is harmless,
    // so this only needs to own the About- and Teams-specific mobile pages.
    // Teams is static markup (teamsMenuHTML): the page only shows it.
    drawer.addEventListener('fnav-nav-drawer:section-change', (e) => {
      clearActive();
      const { section } = e.detail;
      mobileDrawer.classList.toggle('is-teams-page', section === 'teams');
      if (section === 'about') {
        enterMobileAboutMode();
      } else {
        exitMobileAboutMode();
      }
      if (section === 'teams') mobileDrawer.scrollTop = 0;
    });

    function openMobileDrawer() {
      if (mobileDrawerOpen) return;
      mobileDrawerOpen = true;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      // Leaves the promo bar visible above the drawer when opened from the top
      // of the page (node 916:8164); if it's already scrolled away (opened from
      // the sticky bar instead), this is 0 and the drawer takes the full screen.
      const promoBar = document.getElementById('fnav-promo-bar');
      const top = promoBar ? Math.max(0, promoBar.getBoundingClientRect().bottom) : 0;
      mobileDrawer.style.top = `${top}px`;
      mobileScrim.style.top = `${top}px`;

      mobileScrim.style.display = 'block';
      mobileDrawer.style.display = 'flex';
      mobileScrim.style.pointerEvents = 'auto';
      mobileDrawer.style.pointerEvents = 'auto';

      if (mobileDrawerAnim && mobileDrawerAnim.cancel) mobileDrawerAnim.cancel();
      if (mobileScrimAnim && mobileScrimAnim.cancel) mobileScrimAnim.cancel();

      mobileDrawer.style.opacity = '1';
      mobileDrawer.style.transform = 'translateX(0%)';
      mobileScrim.style.opacity = '1';

      const M = Motion;
      if (M && M.animate) {
        mobileDrawerAnim = M.animate(
          mobileDrawer,
          { opacity: [0, 1], transform: ['translateX(-100%)', 'translateX(0%)'] },
          { duration: 0.3, ease: EASE }
        );
        mobileScrimAnim = M.animate(mobileScrim, { opacity: [0, 1] }, { duration: 0.3, ease: EASE });
      }
    }

    function closeMobileDrawer() {
      if (!mobileDrawerOpen) return;
      mobileDrawerOpen = false;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';

      // Closing entirely (e.g. via the scrim) bypasses the close button's
      // color/fabric-mode check — reset here too so the drawer doesn't reopen
      // stuck on that dedicated page next time.
      if (mobileColorMode) {
        mobileColorMode = false;
        mobileShopByColor.classList.remove('is-current');
        mobileChipsWrap.style.display = '';
        mobileDrawer.classList.remove('is-color-page');
        mobileClose.classList.remove('is-back');
        mobileClose.setAttribute('aria-label', 'Close menu');
      }
      if (mobileFabricMode) {
        mobileFabricMode = false;
        mobileShopByFabric.classList.remove('is-current');
        mobileChipsWrap.style.display = '';
        mobileDrawer.classList.remove('is-fabric-page');
        mobileClose.classList.remove('is-back');
        mobileClose.setAttribute('aria-label', 'Close menu');
      }
      if (staticSection) {
        exitMobileAboutMode();
        mobileDrawer.classList.remove('is-teams-page');
        // About / Teams hijack the tab row's is-active without touching
        // currentGender (see setSection) — desktop's setSection resyncs that
        // on its own exit path, but closing the drawer outright (vs. tapping
        // a tab) skips setSection entirely, so it has to happen here instead.
        staticSection = null;
        markStaticSection(null);
        document.querySelectorAll('[data-fnav-drawer]').forEach((link) => {
          link.classList.toggle('is-active', link.dataset.fnavDrawer === currentGender);
        });
      }

      mobileScrim.style.pointerEvents = 'none';
      mobileDrawer.style.pointerEvents = 'none';

      if (mobileDrawerAnim && mobileDrawerAnim.cancel) mobileDrawerAnim.cancel();
      if (mobileScrimAnim && mobileScrimAnim.cancel) mobileScrimAnim.cancel();

      mobileDrawer.style.opacity = '0';
      mobileDrawer.style.transform = 'translateX(-100%)';
      mobileScrim.style.opacity = '0';

      const finish = () => {
        mobileScrim.style.display = 'none';
        mobileDrawer.style.display = 'none';
      };
      const M = Motion;
      if (M && M.animate) {
        mobileDrawerAnim = M.animate(
          mobileDrawer,
          { opacity: [1, 0], transform: ['translateX(0%)', 'translateX(-100%)'] },
          { duration: 0.25, ease: EASE }
        );
        mobileScrimAnim = M.animate(mobileScrim, { opacity: [1, 0] }, { duration: 0.25, ease: EASE });
        setTimeout(finish, 250);
      } else {
        finish();
      }
    }

    mobileHamburgers.forEach((btn) => btn.addEventListener('click', openMobileDrawer));
    mobileClose.addEventListener('click', () => {
      if (mobileColorMode) {
        exitMobileColorMode();
      } else if (mobileFabricMode) {
        exitMobileFabricMode();
      } else {
        closeMobileDrawer();
      }
    });
    mobileScrim.addEventListener('click', closeMobileDrawer);
  })();

  // Search drawer (node 523:107358) — opened by any of the 3 search icons.
  // "Rafaela" is the only query wired to real matches (per the request); any
  // other non-empty query falls into the Error state while typing, or the
  // Unsuccessful Result page on submit. Submitting a query that does match
  // goes to the Successful Result page instead (see showSearchResults below).
  (() => {
    // The product card's image in the Search artboard (node 523:107358) is
    // Figma's own generic "missing image" placeholder graphic — literally the
    // same imageRef repeated on every single card there, never swapped for
    // real photography. Matching that exactly here rather than substituting
    // real product photos of our own.
    const PLACEHOLDER_IMAGE = asset('nav-search-placeholder.png');
    const TRENDING_PRODUCTS = [
      { name: 'Women’s Catarina scrub top™', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Women’s Isabel High waisted scrub pants™', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Women’s Casma scrub top™', price: '$42.00', image: PLACEHOLDER_IMAGE },
    ];
    // All 5 share the same match word ("Rafaela") so the highlight logic has
    // something to find regardless of which card — matches the Figma example,
    // which likewise repeats one product 5 times rather than inventing 5 reals.
    const RAFAELA_PRODUCTS = [
      { name: 'Women’s Rafaela Oversized scrub top™ — Black', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Women’s Rafaela Oversized scrub top™ — Graphite', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Women’s Rafaela Oversized scrub top™ — Ceil Blue', price: '$44.00', image: PLACEHOLDER_IMAGE },
      { name: 'Women’s Rafaela Oversized scrub top™ — Navy', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Women’s Rafaela Oversized scrub top™ — Pewter', price: '$44.00', image: PLACEHOLDER_IMAGE },
    ];
    const MATCH_WORD = 'rafaela';

    const scrim = document.getElementById('fnav-search-drawer-scrim');
    const drawer = document.getElementById('fnav-search-drawer');
    const closeBtn = document.getElementById('fnav-search-drawer-close');
    const input = document.getElementById('fnav-search-input');
    const clearBtn = document.getElementById('fnav-search-clear');
    const searchIcon = drawer.querySelector('.fnav-search-bar-icon');
    const errorMessage = document.getElementById('fnav-search-error-message');
    const heading = document.getElementById('fnav-search-drawer-heading');
    const productsEl = document.getElementById('fnav-search-products');
    const helpEl = document.getElementById('fnav-search-help');
    const EASE = [0.4, 0, 0.2, 1];

    let searchDrawerOpen = false;
    let searchDrawerAnim = null;
    let searchScrimAnim = null;

    // Finds `word` case-insensitively inside `text` and splits it into
    // [before, match, after] so the match can be wrapped in its own span —
    // falls back to [text, '', ''] (no highlight) if it isn't present.
    function splitOnMatch(text, word) {
      const idx = text.toLowerCase().indexOf(word.toLowerCase());
      if (idx === -1) return [text, '', ''];
      return [text.slice(0, idx), text.slice(idx, idx + word.length), text.slice(idx + word.length)];
    }

    function renderProducts(list, highlightQuery) {
      productsEl.innerHTML = '';
      list.forEach((product) => {
        const card = document.createElement('a');
        card.href = labProductHref(product.name) || '#';
        card.className = 'fnav-search-product-card';

        const image = document.createElement('div');
        image.className = 'fnav-search-product-image';
        image.style.backgroundImage = `url(${product.image})`;

        const copy = document.createElement('div');
        copy.className = 'fnav-search-product-copy';

        const name = document.createElement('span');
        name.className = 'fnav-search-product-name';
        const price = document.createElement('span');
        price.className = 'fnav-search-product-price';
        price.textContent = product.price;

        if (highlightQuery) {
          const [before, match, after] = splitOnMatch(product.name, MATCH_WORD);
          name.classList.add('is-dimmed');
          price.classList.add('is-dimmed');
          name.append(before);
          if (match) {
            const matchSpan = document.createElement('span');
            matchSpan.className = 'is-match';
            matchSpan.textContent = match;
            name.append(matchSpan, after);
          }
        } else {
          name.textContent = product.name;
        }

        copy.append(name, price);
        card.append(image, copy);
        productsEl.appendChild(card);
      });
    }

    // Only "rafaela" (or any non-empty prefix of it, so "raf"/"rafa"/etc. all
    // match while typing) is wired to a real result — anything else is
    // treated as unrecognized, exactly as the request describes.
    function isRafaelaQuery(query) {
      return query.length > 0 && MATCH_WORD.startsWith(query);
    }

    function updateSearchDrawer() {
      const query = input.value.trim().toLowerCase();
      clearBtn.hidden = query.length === 0;

      if (query.length === 0) {
        errorMessage.hidden = true;
        heading.hidden = false;
        helpEl.hidden = true;
        renderProducts(TRENDING_PRODUCTS, false);
      } else if (isRafaelaQuery(query)) {
        errorMessage.hidden = true;
        heading.hidden = true;
        helpEl.hidden = true;
        renderProducts(RAFAELA_PRODUCTS, true);
      } else {
        errorMessage.hidden = false;
        heading.hidden = false;
        helpEl.hidden = false;
        renderProducts(TRENDING_PRODUCTS, false);
      }
    }

    function submitSearch() {
      const query = input.value.trim();
      if (!query) return;
      closeSearchDrawer();
      if (isRafaelaQuery(query.toLowerCase())) {
        showSearchResults({ successful: true, query: 'Rafaela' });
      } else {
        showSearchResults({ successful: false, query, fallback: 'black' });
      }
    }

    function openSearchDrawer() {
      if (searchDrawerOpen) {
        submitSearch();
        return;
      }
      closeOtherOverlays('search');
      searchDrawerOpen = true;
      lockBodyScroll();
      input.value = '';
      updateSearchDrawer();

      const promoBar = document.getElementById('fnav-promo-bar');
      const top = Math.max(0, promoBar.getBoundingClientRect().bottom);
      drawer.style.top = `${top}px`;
      scrim.style.top = `${top}px`;

      scrim.style.pointerEvents = 'auto';
      drawer.style.pointerEvents = 'auto';

      if (searchDrawerAnim && searchDrawerAnim.cancel) searchDrawerAnim.cancel();
      if (searchScrimAnim && searchScrimAnim.cancel) searchScrimAnim.cancel();

      drawer.style.opacity = '1';
      drawer.style.transform = 'translateX(0%)';
      scrim.style.opacity = '1';

      const M = Motion;
      if (M && M.animate) {
        searchDrawerAnim = M.animate(
          drawer,
          { opacity: [0, 1], transform: ['translateX(100%)', 'translateX(0%)'] },
          { duration: 0.3, ease: EASE }
        );
        searchScrimAnim = M.animate(scrim, { opacity: [0, 1] }, { duration: 0.3, ease: EASE });
      }

      setTimeout(() => input.focus(), 50);
    }

    function closeSearchDrawer() {
      if (!searchDrawerOpen) return;
      searchDrawerOpen = false;
      unlockBodyScroll();

      scrim.style.pointerEvents = 'none';
      drawer.style.pointerEvents = 'none';

      if (searchDrawerAnim && searchDrawerAnim.cancel) searchDrawerAnim.cancel();
      if (searchScrimAnim && searchScrimAnim.cancel) searchScrimAnim.cancel();

      drawer.style.opacity = '0';
      drawer.style.transform = 'translateX(100%)';
      scrim.style.opacity = '0';

      const M = Motion;
      if (M && M.animate) {
        searchDrawerAnim = M.animate(
          drawer,
          { opacity: [1, 0], transform: ['translateX(0%)', 'translateX(100%)'] },
          { duration: 0.25, ease: EASE }
        );
        searchScrimAnim = M.animate(scrim, { opacity: [1, 0] }, { duration: 0.25, ease: EASE });
      }
    }

    document.querySelectorAll('.fnav-icon-search').forEach((icon) => {
      icon.addEventListener('click', openSearchDrawer);
    });
    closeBtn.addEventListener('click', closeSearchDrawer);
    scrim.addEventListener('click', closeSearchDrawer);
    searchIcon.addEventListener('click', submitSearch);

    input.addEventListener('input', updateSearchDrawer);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submitSearch();
      }
    });
    clearBtn.addEventListener('click', () => {
      input.value = '';
      updateSearchDrawer();
      input.focus();
    });

    document.addEventListener('overlay:open', (e) => {
      if (e.detail.except !== 'search') closeSearchDrawer();
    });
  })();

  // Search results page (node 1141:87261, "Results Pages") — shown by
  // submitSearch() above in place of the homepage hero/scroll-filler. Both the
  // Successful and Unsuccessful Figma frames reuse identical generic PLP
  // filler content (banner, split-grid labels, featured color gallery,
  // products carousel) — those are built once, statically, in index.html.
  // Only the tagline and the two product grids actually change per search.
  (function () {
    const SWATCH_COLORS = ['#282828', '#763720', '#214a82', '#7e436a', '#6fb9c2', '#a7c260', '#f6cd6c', '#cfb151'];

    // Same placeholder as the search drawer's product cards (see the comment
    // there) — the "Product Listing Card" component on this results page uses
    // the identical imageRef in Figma, so this matches the artboard exactly
    // rather than substituting real product photos of our own.
    const PLACEHOLDER_IMAGE = asset('nav-search-placeholder.png');

    const RAFAELA_GRID_PRODUCTS = [
      { name: 'Rafaela Oversized Scrub Top—Black', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Rafaela Oversized Scrub Top—Graphite', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Rafaela Oversized Scrub Top—Ceil Blue', price: '$44.00', image: PLACEHOLDER_IMAGE },
      { name: 'Rafaela Oversized Scrub Top—Navy', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Rafaela Oversized Scrub Pant—Black', price: '$48.00', image: PLACEHOLDER_IMAGE },
    ];

    const GENERIC_GRID_PRODUCTS = [
      { name: 'Catarina Scrub Top—Black', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Isabel High Waisted Scrub Pant—Black', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Casma Scrub Top—Black', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Catarina Scrub Jumpsuit—Black', price: '$68.00', image: PLACEHOLDER_IMAGE },
      { name: 'Zamora Scrub Top—Black', price: '$38.00', image: PLACEHOLDER_IMAGE },
      { name: 'Weekender Jogger—Black', price: '$54.00', image: PLACEHOLDER_IMAGE },
      { name: 'Catarina Scrub Pant—Black', price: '$42.00', image: PLACEHOLDER_IMAGE },
      { name: 'Zamora Scrub Jumpsuit—Black', price: '$68.00', image: PLACEHOLDER_IMAGE },
    ];

    function buildProductCard(product, index) {
      const card = document.createElement('a');
      card.href = labProductHref(product.name) || '#';
      card.className = 'fnav-results-product-card';

      const imageWrap = document.createElement('div');
      imageWrap.className = 'fnav-results-product-image-wrap';
      const image = document.createElement('div');
      image.className = 'fnav-results-product-image';
      image.style.backgroundImage = `url(${product.image})`;
      imageWrap.appendChild(image);
      if (index % 3 === 0) {
        const badge = document.createElement('span');
        badge.className = 'fnav-results-product-badge';
        badge.textContent = 'New';
        imageWrap.appendChild(badge);
      }

      const info = document.createElement('div');
      info.className = 'fnav-results-product-info';

      const swatches = document.createElement('div');
      swatches.className = 'fnav-results-product-swatches';
      for (let i = 0; i < 5; i += 1) {
        const dot = document.createElement('span');
        dot.className = 'fnav-results-product-swatch';
        dot.style.background = SWATCH_COLORS[(index + i) % SWATCH_COLORS.length];
        swatches.appendChild(dot);
      }
      const more = document.createElement('span');
      more.className = 'fnav-results-product-more-colors';
      more.textContent = '+5';
      swatches.appendChild(more);

      const name = document.createElement('span');
      name.className = 'fnav-results-product-name';
      name.textContent = product.name;

      const meta = document.createElement('div');
      meta.className = 'fnav-results-product-meta';
      const price = document.createElement('span');
      price.className = 'fnav-results-product-price';
      price.textContent = product.price;
      const fabric = document.createElement('span');
      fabric.className = 'fnav-results-product-fabric';
      fabric.textContent = 'FIONx';
      meta.append(price, fabric);

      info.append(swatches, name, meta);
      card.append(imageWrap, info);
      return card;
    }

    function fillGrid(el, products) {
      el.innerHTML = '';
      products.forEach((product, i) => el.appendChild(buildProductCard(product, i)));
    }

    // Repeats the base list (with a varied index passed through so swatch
    // colors/badges still look distinct) to fill out a fuller-looking grid —
    // these are placeholder products either way, same as Figma's own grid,
    // which repeats a single unconfigured card dozens of times.
    function repeatProducts(list, count) {
      const out = [];
      for (let i = 0; i < count; i += 1) out.push(list[i % list.length]);
      return out;
    }

    window.showSearchResults = function showSearchResults({ successful, query, fallback }) {
      const page = document.getElementById('fnav-results-page');
      const taglineText = document.getElementById('fnav-results-tagline-text');
      const productCount = document.getElementById('fnav-results-product-count');
      const grid1 = document.getElementById('fnav-results-product-grid');
      const grid2 = document.getElementById('fnav-results-product-grid-2');
      const carouselTrack = document.getElementById('fnav-results-carousel-track');

      const baseProducts = successful ? RAFAELA_GRID_PRODUCTS : GENERIC_GRID_PRODUCTS;
      const allProducts = repeatProducts(baseProducts, 12);

      if (successful) {
        taglineText.textContent = `Search Results for ‘${query}’`;
        productCount.textContent = `${baseProducts.length} PRODUCTS`;
      } else {
        taglineText.textContent = `No results found for ‘${query}’, showing 22 results for ‘${fallback}’`;
        productCount.textContent = '22 PRODUCTS';
      }

      fillGrid(grid1, allProducts.slice(0, 8));
      fillGrid(grid2, allProducts.slice(8, 12));

      carouselTrack.innerHTML = '';
      repeatProducts(baseProducts, 6).forEach((product, i) => {
        carouselTrack.appendChild(buildProductCard(product, i));
      });

      // In the Navigation artifact the results page replaces its demo hero
      // (which also holds the masthead), leaving the promo bar on top and the
      // compact bar taking over. On a host prototype the equivalent is: lift
      // the promo bar into normal flow at the very top (on the Homepage it
      // sits inside an absolutely-positioned header wrapper), put the results
      // page right after it, and hide the masthead plus everything the host
      // page renders — but never the nav's own fixed layers or the lab
      // toolbar. Hidden elements are tagged so a re-run doesn't re-scan them.
      const promoBar = document.getElementById('fnav-promo-bar');
      if (promoBar) {
        document.body.prepend(promoBar);
        promoBar.after(page);
      } else {
        document.body.prepend(page);
      }
      const keep = (el) =>
        el === promoBar || el === page ||
        (el.id && el.id.startsWith('fnav-') && el.id !== 'fnav-masthead') ||
        el.matches('script, style, link, template, .lab-bar, .lab-bar-handle');
      [...document.body.children].forEach((el) => {
        if (keep(el) || el.dataset.fnavResultsHidden) return;
        el.dataset.fnavResultsHidden = 'true';
        el.style.display = 'none';
      });
      const masthead = document.getElementById('fnav-masthead');
      if (masthead) masthead.style.display = 'none';
      page.hidden = false;
      window.scrollTo(0, 0);
    };
  })();

  // Country/language selector (node 1375:11262) — opened from any of the 3
  // "US | English" instances (promo bar, mobile utility, results-page
  // footer). Same right-side slide-in shell as the account/search drawers;
  // Save just closes the drawer since this prototype has no real geo/locale
  // backend to persist a selection to.
  (() => {
    const scrim = document.getElementById('fnav-country-drawer-scrim');
    const drawer = document.getElementById('fnav-country-drawer');
    const closeBtn = document.getElementById('fnav-country-drawer-close');
    const saveBtn = document.getElementById('fnav-country-drawer-save');
    const EASE = [0.4, 0, 0.2, 1];

    let countryDrawerOpen = false;
    let countryDrawerAnim = null;
    let countryScrimAnim = null;

    function openCountryDrawer() {
      if (countryDrawerOpen) return;
      closeOtherOverlays('country');
      countryDrawerOpen = true;
      lockBodyScroll();

      const promoBar = document.getElementById('fnav-promo-bar');
      const top = Math.max(0, promoBar.getBoundingClientRect().bottom);
      drawer.style.top = `${top}px`;
      scrim.style.top = `${top}px`;

      scrim.style.pointerEvents = 'auto';
      drawer.style.pointerEvents = 'auto';

      if (countryDrawerAnim && countryDrawerAnim.cancel) countryDrawerAnim.cancel();
      if (countryScrimAnim && countryScrimAnim.cancel) countryScrimAnim.cancel();

      drawer.style.opacity = '1';
      drawer.style.transform = 'translateX(0%)';
      scrim.style.opacity = '1';

      const M = Motion;
      if (M && M.animate) {
        countryDrawerAnim = M.animate(
          drawer,
          { opacity: [0, 1], transform: ['translateX(100%)', 'translateX(0%)'] },
          { duration: 0.3, ease: EASE }
        );
        countryScrimAnim = M.animate(scrim, { opacity: [0, 1] }, { duration: 0.3, ease: EASE });
      }
    }

    function closeCountryDrawer() {
      if (!countryDrawerOpen) return;
      countryDrawerOpen = false;
      unlockBodyScroll();

      scrim.style.pointerEvents = 'none';
      drawer.style.pointerEvents = 'none';

      if (countryDrawerAnim && countryDrawerAnim.cancel) countryDrawerAnim.cancel();
      if (countryScrimAnim && countryScrimAnim.cancel) countryScrimAnim.cancel();

      drawer.style.opacity = '0';
      drawer.style.transform = 'translateX(100%)';
      scrim.style.opacity = '0';

      const M = Motion;
      if (M && M.animate) {
        countryDrawerAnim = M.animate(
          drawer,
          { opacity: [1, 0], transform: ['translateX(0%)', 'translateX(100%)'] },
          { duration: 0.25, ease: EASE }
        );
        countryScrimAnim = M.animate(scrim, { opacity: [1, 0] }, { duration: 0.25, ease: EASE });
      }
    }

    document.querySelectorAll('.fnav-js-open-country-drawer').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openCountryDrawer();
      });
    });
    closeBtn.addEventListener('click', closeCountryDrawer);
    scrim.addEventListener('click', closeCountryDrawer);
    saveBtn.addEventListener('click', closeCountryDrawer);

    document.addEventListener('overlay:open', (e) => {
      if (e.detail.except !== 'country') closeCountryDrawer();
    });
  })();

  // The bag — the same right-side shell as the account and country drawers,
  // so it shares their scroll lock and closes them (and is closed by them).
  mountFigsCart({ lockBodyScroll, unlockBodyScroll, closeOtherOverlays });

  // The account flow (Figma Account 1838:21977, signed in 1174:108730) —
  // the same shell again: the Account icons and the My Account links open it.
  mountFigsAccount({ lockBodyScroll, unlockBodyScroll, closeOtherOverlays });
}
